import * as THREE from 'three';
import QRCode from 'qrcode';
import { InputController, type InputFrame } from '../core/InputController';
import { Loop } from '../core/Loop';
import { createRenderer, resizeRenderer } from '../core/Renderer';
import { Apparition } from '../entities/Apparition';
import { Birds } from '../entities/Birds';
import { Cat } from '../entities/Cat';
import { Hazards } from '../entities/Hazards';
import { createNightEnvironment } from '../world/Surface';
import { CharacterModel } from '../entities/CharacterModel';
import { VrmCharacter } from '../entities/VrmCharacter';
import { Heroine, wrapAngle } from '../entities/Heroine';
import { Stranger, type StrangerContext } from '../entities/Stranger';
import { AudioSystem } from '../systems/AudioSystem';
import { CameraRig } from '../systems/CameraRig';
import { DebugTools, type DebugTuning } from '../systems/DebugTools';
import { Hud } from '../systems/Hud';
import { PostFX } from '../systems/PostFX';
import { Weather } from '../systems/Weather';
import { Footprints } from '../systems/Footprints';
import { Unease, type UneaseFrame } from '../systems/Unease';
import { PhoneScares, type ScareAction, type ScareFrame } from '../systems/PhoneScares';
import { PhoneFx, type CallStage } from '../ui/PhoneFx';
import { Sandbox, type SandboxActions } from '../systems/Sandbox';
import { PhoneLink } from '../net/PhoneLink';
import type { GameEvent } from '../net/protocol';
import { createSeededRandom } from '../utils/random';
import { PropLibrary, disposeProps } from '../world/Props';
import { WorldBuilder, type WorldBuild } from '../world/WorldBuilder';
import type { LightSource } from '../world/LightSystem';
import { LEVELS, zoneFor } from '../levels';
import type { HeroineLook, LevelContext, LevelDef, RuleZone } from '../levels/types';
import { Campaign } from './Campaign';
import { Directions } from './Directions';
import { CELL, CityMap, type CellRef } from './map';
import { MOM_NAME, MomTexts } from './MomTexts';
import { Trail } from './Trail';

/** How far the title camera drifts side to side. */
const TITLE_SWAY = 0.6;
/**
 * Her noticing him: seconds the camera holds on him, the height it aims at (his
 * face), how far off she notices him in the dark / under a light (m), the gap
 * between glances, how long he must be gone before it can happen again (s),
 * and the most per night.
 */
const GLANCE_TIME = 2.4;
const GLANCE_HEIGHT = 1.55;
const GLANCE_NEAR = 13;
const GLANCE_FAR = 30;
const GLANCE_COOLDOWN = 30;
const GLANCE_REARM = 8;
const GLANCE_MAX = 4;
const IDENTITY = new THREE.Matrix4();
/** Phone pictures (his face, the photo of her): size, and how bright they come out. */
const SNAP_W = 240;
const SNAP_H = 416;
const FACE_EXPOSURE = 0.95;
const PHOTO_EXPOSURE = 2.4;
/** Face portrait: how far above the head bone his face is, and how close the camera is (m). */
const FACE_LIFT = 0.12;
/** Pause between background outfit preloads, so they don't crowd the first frames (ms). */
const PRELOAD_GAP_MS = 1500;
const FACE_DIST =0.72;

type Phase = 'title' | 'playing' | 'paused' | 'caught' | 'unlocking' | 'home';

/** Lines every night can use; a level's own `lines` override these by key. */
const BASE_LINES: Record<string, string> = {
  intro: 'Just get home.',
  firstSight: 'Someone is walking behind me.',
  figure: 'Was someone… standing down there?',
  callScare: 'That was him. He— he has my number. How does he have my number?',
  flashScare: '…No. I didn\'t see that. I didn\'t see that.',
  scareAgain: 'Stop it. Stop it stop it stop it.',
  stopped: '…He stopped when I stopped.',
  phone: 'He\'s looking at his phone. Just someone walking home. …Right?',
  flicker: 'Come on… not now.',
  sensor: 'A sensor light — right behind me. Now he can see me.',
  wrongBuilding: 'This isn\'t my building.',
  unlock: 'Come on, come on…',
  t45: 'Is he getting closer?',
  t70: 'Don\'t look back. Just walk.',
  t92: 'He\'s right behind me.',
  vanished: 'Where did he go?',
  back: 'He\'s back.',
  still: 'I can\'t just stand here.',
  darkSlow: 'I can barely see where I\'m going… My phone has a flashlight.',
  torchOff: 'Flashlight off. I need to save the battery.',
  torchAct3: 'My flashlight… he can just follow the light. In the dark he loses me.',
  unwatched: 'I haven\'t looked back in a while… his footsteps sound closer.',
  unwatched2: 'Check behind you. Check behind you…',
  batt10: '10%… the flashlight is eating my battery.',
  batt5: '5%. If my phone dies, I\'m alone out here.',
  batt2: 'Please don\'t die… please…',
  momFirst: 'Mom\'s texting. I have to answer her…',
  momWorried: 'Now she\'s worried. She won\'t stop texting…',
  momPutAway: 'I\'ll answer her later. I hope.',
  momIgnored: 'I didn\'t answer her… His footsteps sound faster.',
  momIgnoredAgain: 'Mom again. And he\'s closer.',
  dogSleeping: 'A dog, asleep by its kennel. Quietly… don\'t wake it.',
  dogBark: 'Ah—! Stupid dog… my heart…',
  dogBark2: 'Another one! Shh, shh…',
  bump: 'Ow— sorry, sorry…',
  bump2: 'Watch where you\'re going!',
};

/** Battery drain in percent per second. */
const DRAIN = { standby: 0.008, flashlight: 0.09, map: 0.06, texting: 0.08 };
const DEAD_PHONE_TEXTS = [
  'Are you ok?',
  'Where are you?',
  'Why is your phone off?',
  'Please call me.',
  'Are you ok?',
  'Where are you??',
  "I'm calling the police.",
];

type Note = { from: string; text: string; tone?: 'normal' | 'system' | 'sent' };
const NOTES = {
  momLate: { from: 'お母さん · Mom', text: 'Are you home yet?' },
  home: { from: 'You', text: "I'm home.", tone: 'sent' },
} satisfies Record<string, Note>;

export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(60, 1, 0.1, 600);
  private readonly campaign = new Campaign();
  private level: LevelDef = LEVELS[0];
  private map!: CityMap;
  private world: WorldBuild | null = null;
  private catEnabled = false;
  private hemi = new THREE.HemisphereLight('#4a5c86', '#0b0a08', 0.7);
  private ambient = new THREE.AmbientLight('#1a2236', 0.5);
  private readonly heroine = new Heroine();
  private readonly stranger = new Stranger();
  private readonly cat = new Cat();
  private readonly hazards = new Hazards();
  private readonly birds = new Birds();
  /** Her VRoid models by look, loaded on first use; and the one this night wants. */
  private readonly heroineModels = new Map<HeroineLook, Promise<VrmCharacter>>();
  private heroineLook: HeroineLook = 'school';
  /** What the chosen night still needs before she can set off, with download progress (0..1). */
  private readonly loading = new Map<string, number>([['stranger', 0]]);
  /** A background outfit preload is in flight. */
  private preloading = false;
  /** Street clutter models (loaded once) and this night's instances of them. */
  private props: PropLibrary | null = null;
  private propGroup: THREE.Group | null = null;
  /** Title scene: she walks into the shot, stops, and takes out her phone. */
  private titleTime = 0;
  private titleStopAt = -1;
  private readonly titleSpot = new THREE.Vector3();
  /** Her map app's route home (rebuilt per level). */
  private directions!: Directions;
  /** Title camera's resting spot (it sways around this), chosen so nothing blocks the shot. */
  private readonly titleCam = new THREE.Vector3();
  /** Test hook: camera offset from her (and look-at point), for close-ups. */
  private debugCam: { offset: THREE.Vector3; look: THREE.Vector3 } | null = null;
  /** Frozen by a barking dog until this time. */
  private startleUntil = 0;
  /** Her noticing him: until when the camera holds on him, how many times tonight, and whether he has been out of sight since. */
  private glanceUntil = 0;
  private glanceCount = 0;
  private lastGlanceAt = -99;
  private glanceReady = true;
  private hiddenSince = 0;
  private readonly glanceAt = new THREE.Vector3();
  private startleAt = new THREE.Vector3();
  /** Staggering after a bump until this time. */
  private staggerUntil = 0;
  private dogBarks = 0;
  private bumps = 0;
  private readonly trail = new Trail();
  private readonly rig: CameraRig;
  private readonly post: PostFX;
  private readonly hud = new Hud();
  private readonly link = new PhoneLink();
  private readonly mom = new MomTexts({
    received: (_text, first, kind) => {
      // Her phone buzzes and she reads it: the chat comes up.
      if (this.phase === 'playing') this.chatOpen = true;
      this.chatCloseAt = 0;
      this.audio.textPing();
      this.buzz([40, 80, 40]);
      if (first) this.sayOnce('momFirst');
      // A worried follow-up means more time with her eyes on the screen.
      if (kind === 'question') {
        this.spike(0.025);
        this.sayOnce('momWorried');
      }
    },
    sent: () => undefined,
    ended: () => {
      this.chatCloseAt = this.elapsed + 1.8;
    },
    ignored: (count) => {
      this.stranger.boost(18);
      this.spike(0.05);
      this.sayNow(count === 1 ? 'momIgnored' : 'momIgnoredAgain', 3.4);
    },
  });
  private readonly audio = new AudioSystem();
  /** The small wrong things: extra footsteps, whispers, far-off sounds, stuttering lamps, the figure down the street. */
  private readonly apparition = new Apparition();
  private readonly unease = new Unease(this.audio, this.apparition);
  private uframe: UneaseFrame | null = null;
  /** 0..1 how much bamboo is around her cell (it knocks in the wind). */
  private nearBamboo = 0;
  /** Her phone turning on her: texts from nobody, the call that answers itself, his face over the map. */
  private readonly phoneFx = new PhoneFx();
  private readonly scares = new PhoneScares();
  private scareFrame: ScareFrame | null = null;
  /** His face, flash-lit, for the phone scares (rendered once his model is in), and as a JPEG for a linked phone. */
  private strangerFace: HTMLCanvasElement | null = null;
  private faceData = '';
  private readonly snapCam = new THREE.PerspectiveCamera(35, 0.75, 0.05, 300);
  private snapTarget: THREE.WebGLRenderTarget | null = null;
  private studio: { scene: THREE.Scene; flash: THREE.PointLight } | null = null;
  private readonly snapColor = new THREE.Color();
  /** Dev sandbox: trigger scares on demand; optionally she can't be caught and Mom stays quiet. */
  private sandbox: Sandbox | null = null;
  private sandboxSafe = false;
  private sandboxQuiet = false;
  private weather!: Weather;
  private readonly footprints = new Footprints();
  /** Level crossing: idle → warn (bell) → closed (gates down, train) → cooldown. */
  private crossingState: 'idle' | 'warn' | 'closed' | 'cooldown' = 'idle';
  private crossingTime = 0;
  private bellTimer = 0;
  private endingKey = 'home';
  private mirrorCooldown = 0;
  private ghostUntil = 0;
  private ghost: THREE.Mesh | null = null;
  private readonly input: InputController;
  private readonly debugTools: DebugTools;
  private readonly moon = new THREE.DirectionalLight('#8ea6d8', 0.5);
  private readonly fill = new THREE.PointLight('#9fb3dd', 0.4, 5, 1.8);
  private readonly loop = new Loop(
    (dt, elapsed) => this.update(dt, elapsed),
    () => this.render(),
  );
  private readonly tuning: DebugTuning = { exposure: 1.15, maxDpr: 1.75, tensionScale: 1, bloom: 0.6, ambientOcclusion: !window.matchMedia('(pointer: coarse)').matches };

  private phase: Phase = 'title';
  private frame = 0;
  private time = 0;
  private elapsed = 0;
  private tension = 0;
  private fearVisual = 0;
  private peakTension = 0;
  private maxProgress = 0;
  private rng = createSeededRandom(1);
  private field: Int16Array = new Int16Array(1);
  private homeField: Int16Array = new Int16Array(1);
  private startHomeDist = 1;
  private startCell: CellRef = { c: 0, r: 0 };
  private homeCell: CellRef = { c: 0, r: 0 };
  private cell: CellRef = { c: 0, r: 0 };
  private prevDir: { dc: number; dr: number } | null = null;
  /** Light-rule zone she is in (1 stay in the light, 2 mixed, 3 flipped). */
  private act: RuleZone = 1;
  private lit = false;
  private lightLevel = 0;
  private inShrine = false;
  private visitedShrine = false;
  private phoneOpen = false;
  private phoneLocal = false;
  /** The messages app is up (Mom texted). */
  private chatOpen = false;
  private chatCloseAt = 0;
  private phoneApp: 'none' | 'map' | 'chat' = 'none';
  private battery = 18;
  /** She has switched the flashlight on (it lights only while the phone apps are closed). */
  private flashlightWanted = false;
  private inDark = false;
  private darkHintUntil = 0;
  private flashlightOn = false;
  private unwatchedSaidAt = -999;
  private unwatchedHintUntil = 0;
  private torchHintUntil = 0;
  private readonly batteryWarned = new Set<number>();
  private caughtCause: 'caught' | 'battery' = 'caught';
  private deadTextIndex = 0;
  private deadTextTimer = 0;
  private pendingNotes: { at: number; id: string; from: string; text: string; tone?: 'normal' | 'system' | 'sent' }[] = [];
  private readonly noted = new Set<string>();
  private wasLookingBack = false;
  private lookingBack = false;
  private strangerVisible = false;
  private lastSeenAt = -99;
  private vanishedRecently = false;
  private spawnTimer = 0;
  private unlockProgress = 0;
  private endTime = 0;
  private endShown = false;
  private readonly said = new Set<string>();
  private pendingLines: { at: number; key: string }[] = [];
  private stats = { lookBacks: 0, closest: Infinity, litTime3: 0, darkTime12: 0, stillTime: 0, torchTime3: 0, dogBarks: 0, bumps: 0, unwatchedTime: 0 };
  private pausedForScreenshot = false;
  private reducedMotion = false;
  private howToOpen = false;
  private readonly frustum = new THREE.Frustum();
  private readonly projScreen = new THREE.Matrix4();
  private readonly tmpV = new THREE.Vector3();
  private readonly sctx: StrangerContext;
  private readonly ctx: LevelContext;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = createRenderer(canvas);
    this.renderer.toneMappingExposure = this.tuning.exposure;
    this.renderer.info.autoReset = false;
    this.rig = new CameraRig(this.camera);
    this.post = new PostFX(this.renderer, this.scene, this.camera);

    const get = (s: string) => {
      const e = document.querySelector<HTMLElement>(s);
      if (!e) throw new Error(`Missing ${s}`);
      return e;
    };
    this.input = new InputController(canvas, {
      stick: get('#touch-stick'),
      knob: get('#touch-knob'),
      back: get('#touch-back'),
      phone: get('#touch-phone'),
      light: get('#touch-light'),
    });
    this.debugTools = new DebugTools(this.tuning, () => {
      this.renderer.toneMappingExposure = this.tuning.exposure;
      this.post.setBloom(this.level.theme.daylight ? this.tuning.bloom * 0.35 : this.tuning.bloom);
      this.post.setAo(this.tuning.ambientOcclusion);
      this.forceResize();
    });

    this.setupScene();
    this.weather = new Weather(this.scene);
    this.scene.add(this.footprints.mesh);
    this.loadCharacters();
    this.map = new CityMap(this.campaign.level.map);
    this.directions = new Directions(this.map, this.campaign.level.gps ?? 'good');

    this.sctx = {
      dt: 0,
      tension: 0,
      act: 1,
      playerPos: this.heroine.position,
      playerForward: this.heroine.forward,
      playerSpeed: 0,
      playerLit: false,
      flashlight: false,
      playerInShrine: false,
      lookingBack: false,
      visible: false,
      field: this.field,
      map: this.map,
      rng: () => this.rng(),
      trailPointBehind: (d, out) => this.trail.pointBehind(this.heroine.position, d, out),
      isPointVisible: (p) => this.isPointVisible(p),
    };
    this.ctx = this.createLevelContext();

    this.heroine.onStep = (running) => this.audio.footstepHer(running);
    this.stranger.onStep = (running) => this.strangerFootstep(running);

    this.loadLevel(this.campaign.level);
    this.hud.setNights(LEVELS, this.campaign.unlocked, this.campaign.current, (i) => this.selectNight(i));

    this.bindButtons();
    this.setupPhoneLink();
    this.link.onReply = (id, choice) => {
      if (this.phase === 'playing' && this.mom.awaitingReply) {
        this.chatOpen = true;
        this.mom.reply(choice, id);
      }
    };
    this.hud.onMomReply = (choice) => {
      if (this.phase === 'playing' && this.mom.awaitingReply) {
        this.chatOpen = true;
        this.mom.reply(choice);
      }
    };
    // A phone that doesn't have this night's map asks for it.
    this.link.onHello = () => this.sendLevelToPhone();
    this.link.onPutAway = () => {
      this.putAwayPhone();
    };
    // Apps tapped on the real phone's home screen.
    this.link.onOpenApp = (app) => {
      if (this.phase !== 'playing') return;
      if (app === 'map') {
        this.chatOpen = false;
        this.phoneLocal = true;
      } else if (this.mom.view()) {
        this.chatOpen = true;
        this.chatCloseAt = 0;
      }
    };
    this.unease.onFigureGone = () => {
      // Only a line: the figure is atmosphere, it doesn't feed his pursuit.
      if (this.phase === 'playing') this.sayOnce('figure');
    };
    this.link.onTorch = () => {
      if (this.phase === 'playing') this.toggleFlashlight();
    };
    this.resetRun();
    this.phase = 'title';
    this.startTitleScene();
    this.hud.showTitle(true);
    this.hud.setHud(false);
    this.hud.fade(0, 2.5);
    this.forceResize();
    // Public builds keep the cheat-capable hooks off unless asked for (?test).
    if (import.meta.env.DEV || new URLSearchParams(location.search).has('test')) this.installTestHooks();
    if (Sandbox.wanted()) this.sandbox = new Sandbox(this.sandboxActions());
    this.publishDiagnostics();
  }

  start(): void {
    this.loop.start();
  }

  dispose(): void {
    this.loop.stop();
    this.input.dispose();
    this.audio.dispose();
    this.weather.dispose();
    this.debugTools.dispose();
    this.sandbox?.dispose();
    this.heroine.dispose();
    // The models she isn't wearing (heroine.dispose() handled the one she is).
    for (const load of this.heroineModels.values()) {
      void load.then((m) => {
        if (m.root.parent === null) m.dispose();
      }, () => undefined);
    }
    this.stranger.dispose();
    this.cat.dispose();
    this.hazards.dispose();
    this.apparition.dispose();
    this.scene.environment?.dispose();
    this.world?.dispose();
    this.post.dispose();
    this.renderer.dispose();
    window.__THREE_GAME_DIAGNOSTICS__ = undefined;
    window.__THREE_GAME_TEST_HOOKS__ = undefined;
  }

  // ------------------------------------------------------------ setup

  /**
   * Compile every material now (on the title screen), including ones that are
   * hidden until later: her phone, the sensor light, the flashlight. Otherwise
   * the first time something appears the game hitches while shaders compile.
   */
  private prewarmShaders(): void {
    const hidden: THREE.Object3D[] = [];
    this.scene.traverse((o) => {
      if (!o.visible) {
        hidden.push(o);
        o.visible = true;
      }
    });
    this.renderer.compile(this.scene, this.camera);
    for (const o of hidden) o.visible = false;
  }

  /** Load the skinned character models; the procedural figures stand in until then (or if it fails). */
  private loadCharacters(): void {
    const base = import.meta.env.BASE_URL;
    // Her VRoid model is loaded per night by loadLevel() (dressHeroine).
    void CharacterModel.load({
      url: `${base}models/stranger.glb`,
      height: 1.86,
      tints: {
        Skin: '#86705f',
        Purple: '#16181c', // hoodie
        LightBlue: '#111216', // trousers
        White: '#232528', // shoes
        Hair: '#0b0b0c',
        Eyebrows: '#0b0b0c',
        Eye: '#0b0b0c',
      },
      // Hoodie sleeves and full-length trousers.
      coverSkin: [
        { ifMeshHas: 'Purple', color: '#16181c' },
        { ifMeshHas: 'LightBlue', color: '#111216' },
        { ifMeshHas: 'White', color: '#111216' },
      ],
    })
      .then((m) => {
        // A black surgical mask: the one thing that marks him out in a crowd.
        m.addFaceMask('#0d0d10');
        this.stranger.attachModel(m);
        this.prewarmShaders();
        // His portrait for the phone, taken now (while loading) so the scare itself never hitches.
        this.setStrangerFace();
      })
      .catch((e) => console.warn('Stranger model failed to load; using the procedural figure.', e))
      .finally(() => this.loading.delete('stranger'));
    this.loadPassersby(base);
    this.loadScenery(base);
    this.hazards
      .loadDogModels(`${base}models/dogs/`)
      .then(() => this.prewarmShaders())
      .catch((e) => console.warn('Dog model failed to load; using the procedural dog.', e));
  }

  /** Street clutter and the crows; each appears as soon as it arrives. */
  private loadScenery(base: string): void {
    PropLibrary.load(`${base}models/props/`)
      .then((lib) => {
        this.props = lib;
        this.placeProps();
        this.prewarmShaders();
      })
      .catch((e) => console.warn('Street props failed to load.', e));
    this.birds.onSound = (kind, x, z) => {
      const h = this.heroine.position;
      const dx = x - h.x;
      const dz = z - h.z;
      const d = Math.hypot(dx, dz);
      const vol = Math.pow(THREE.MathUtils.clamp(1 - d / 45, 0, 1), 1.2) * (1 - this.weather.rainLevel * 0.5);
      const camRight = this.tmpV.set(1, 0, 0).applyQuaternion(this.camera.quaternion);
      const pan = d > 0.01 ? ((dx * camRight.x + dz * camRight.z) / d) * 0.8 : 0;
      if (kind === 'flap') this.audio.wingFlap(vol, pan);
      else this.audio.caw(vol, pan);
    };
    this.birds
      .load(`${base}models/props/bird.glb`)
      .then(() => this.prewarmShaders())
      .catch((e) => console.warn('Crow model failed to load.', e));
  }

  /** Put this night's clutter in the world (once both the world and the models exist). */
  private placeProps(): void {
    if (this.propGroup) {
      this.scene.remove(this.propGroup);
      disposeProps(this.propGroup);
      this.propGroup = null;
    }
    if (!this.props || !this.world) return;
    this.propGroup = this.props.build(this.world.props);
    this.scene.add(this.propGroup);
  }

  /** Commuters for the passer-by pool: light office clothes, nothing like his. */
  private loadPassersby(base: string): void {
    void CharacterModel.load({
      url: `${base}models/stranger.glb`,
      height: 1.74,
      tints: {
        Skin: '#d2ac8e',
        Purple: '#6d727b', // grey suit jacket
        LightBlue: '#4a4f58',
        White: '#2a2420',
        Hair: '#2a211c',
        Eyebrows: '#2a211c',
        Eye: '#0b0b0c',
      },
      coverSkin: [
        { ifMeshHas: 'Purple', color: '#6d727b' },
        { ifMeshHas: 'LightBlue', color: '#4a4f58' },
        { ifMeshHas: 'White', color: '#4a4f58' },
      ],
    })
      .then((m) => {
        this.hazards.addWalkerModel(m);
        this.prewarmShaders();
      })
      .catch((e) => console.warn('Passer-by model failed to load.', e));
    void CharacterModel.load({
      url: `${base}models/heroine.glb`,
      height: 1.6,
      tints: {
        Skin: '#e0bb9c',
        White: '#d9d4ca',
        Orange: '#8a7660', // beige coat
        Grey: '#2a2622',
        Hair_Brown: '#3a2a20',
        Hair_Blond: '#3a2a20',
        Brown: '#3a2a20',
      },
      coverSkin: [
        { ifMeshHas: 'White', color: '#8a7660' },
        { ifMeshHas: 'Grey', color: '#2a2622' },
      ],
    })
      .then((m) => {
        this.hazards.addWalkerModel(m);
        this.prewarmShaders();
      })
      .catch((e) => console.warn('Passer-by model failed to load.', e));
  }

  /**
   * Put her in this night's model: an anime-style VRoid model with retargeted
   * animations. Each look is fetched only when a night needs it, then kept. A
   * missing look falls back to the school uniform; if that fails too, the
   * low-poly glTF stands in.
   */
  /** Her model for a look: loaded once, then cached (whether worn now or preloaded for a later night). */
  private loadLook(look: HeroineLook): Promise<VrmCharacter> {
    let load = this.heroineModels.get(look);
    if (load) return load;
    const base = import.meta.env.BASE_URL;
    const key = `look:${look}`;
    load = VrmCharacter.load(
      `${base}models/heroine-${look}.vrm`,
      `${base}models/heroine-anims.glb`,
      {
        idle: 'Idle_Loop',
        walk: 'Walk_Formal_Loop',
        run: 'Jog_Fwd_Loop',
        oneShots: { HitRecieve: 'Hit_Chest' },
      },
      (e) => this.loading.has(key) && this.loading.set(key, e.lengthComputable && e.total > 0 ? e.loaded / e.total : 0.5),
    );
    this.heroineModels.set(look, load);
    load.then(
      () => this.loading.delete(key),
      () => {
        this.loading.delete(key);
        this.heroineModels.delete(look);
      },
    );
    return load;
  }

  private dressHeroine(look: HeroineLook): void {
    this.heroineLook = look;
    const base = import.meta.env.BASE_URL;
    // Not in yet: the chosen night waits for it (the title's Play button shows the progress).
    if (!this.heroineModels.has(look)) this.loading.set(`look:${look}`, 0);
    const load = this.loadLook(look);
    load
      .then((m) => {
        if (this.heroineLook !== look) return;
        this.heroine.attachModel(m);
        this.prewarmShaders();
        this.schedulePreload();
      })
      .catch((e) => {
        if (this.heroineLook !== look) return;
        if (look !== 'school') {
          console.warn(`Her "${look}" model failed to load; using the school uniform.`, e);
          this.dressHeroine('school');
          return;
        }
        console.warn('VRM heroine failed to load; using the low-poly model.', e);
        this.loadLowPolyHeroine(base);
      });
  }

  /** She is ready to set off: this night's outfit and the stranger are loaded. */
  private get ready(): boolean {
    return this.loading.size === 0;
  }

  /** 0..1 of what the chosen night is still waiting for. */
  private get loadFraction(): number {
    if (this.loading.size === 0) return 1;
    let sum = 0;
    for (const f of this.loading.values()) sum += f;
    return sum / this.loading.size;
  }

  /**
   * Once the current night is ready, load the other outfits in the background
   * (the nights coming next first), one at a time, and compile their shaders
   * against tonight's lights so changing nights never waits or stutters. On a
   * phone, only the next one (memory is tight).
   */
  private schedulePreload(): void {
    if (this.preloading) return;
    const order: HeroineLook[] = [];
    for (let i = 1; i < LEVELS.length; i += 1) {
      const look = LEVELS[(this.campaign.current + i) % LEVELS.length].look ?? 'school';
      if (!order.includes(look) && !this.heroineModels.has(look)) order.push(look);
    }
    const queue = window.matchMedia('(pointer: coarse)').matches ? order.slice(0, 1) : order;
    const look = queue[0];
    if (!look) return;
    this.preloading = true;
    window.setTimeout(() => {
      this.loadLook(look)
        .then((m) => this.renderer.compileAsync(m.root, this.camera, this.scene))
        .catch((e) => console.warn(`Preloading her "${look}" model failed`, e))
        .finally(() => {
          this.preloading = false;
          this.schedulePreload();
        });
    }, PRELOAD_GAP_MS);
  }

  private loadLowPolyHeroine(base: string): void {
    void CharacterModel.load({
      url: `${base}models/heroine.glb`,
      height: 1.62,
      tints: {
        Skin: '#e6c3a6',
        White: '#3b4260', // cuffs
        Orange: '#3b4260', // outfit
        Grey: '#151518', // shoes
        Hair_Brown: '#120e10',
        Hair_Blond: '#120e10',
        Brown: '#2a1d17',
      },
      // Long sleeves over the bare arms, dark tights at the ankles.
      coverSkin: [
        { ifMeshHas: 'White', color: '#3b4260' },
        { ifMeshHas: 'Grey', color: '#1b1a1f' },
      ],
    })
      .then((m) => {
        this.heroine.attachModel(m);
        this.prewarmShaders();
      })
      .catch((e) => console.warn('Heroine model failed to load; using the procedural figure.', e));
  }

  private setupScene(): void {
    this.scene.background = new THREE.Color('#04060b');
    // Dim city reflections for wet streets, glass and metal.
    this.scene.environment = createNightEnvironment(this.renderer);
    this.scene.environmentIntensity = 0.6;
    this.scene.fog = new THREE.FogExp2('#060912', 0.034);
    this.scene.add(this.hemi, this.ambient);
    // Soft fill that keeps her readable in the dark without lighting the street.
    this.fill.position.set(0, 2.4, 1.5);
    this.heroine.group.add(this.fill);
    this.moon.position.set(-30, 60, -40);
    this.moon.castShadow = true;
    this.moon.shadow.mapSize.set(2048, 2048);
    this.moon.shadow.camera.near = 1;
    this.moon.shadow.camera.far = 140;
    this.moon.shadow.camera.left = -26;
    this.moon.shadow.camera.right = 26;
    this.moon.shadow.camera.top = 26;
    this.moon.shadow.camera.bottom = -26;
    this.moon.shadow.bias = -0.0006;
    this.moon.shadow.normalBias = 0.04;
    this.scene.add(this.moon, this.moon.target);
    this.scene.add(this.heroine.group, this.stranger.group, this.cat.group, this.hazards.group, this.birds.group, this.apparition.group);
    this.scene.add(this.heroine.sceneLights, this.stranger.sceneLights);
  }

  /**
   * Build a night: its streets, lights, story hooks, Mom script and stranger
   * tuning. Safe to call again to switch nights (the old world is disposed).
   */
  private loadLevel(level: LevelDef): void {
    this.level = level;
    if (this.world) {
      this.scene.remove(this.world.group);
      this.world.dispose();
    }
    this.map = new CityMap(level.map);
    this.directions = new Directions(this.map, level.gps ?? 'good');
    this.world = new WorldBuilder(this.map, level.theme).build();
    this.scene.add(this.world.group);
    this.placeProps();
    this.birds.setPerches(this.world.perches);

    const t = level.theme;
    this.scene.background = new THREE.Color(t.sky);
    this.scene.fog = new THREE.FogExp2(t.fog.color, t.fog.density);
    this.hemi.color.set(t.hemi.sky);
    this.hemi.groundColor.set(t.hemi.ground);
    this.hemi.intensity = t.hemi.intensity;
    this.ambient.intensity = t.ambient;
    this.moon.intensity = t.moon;
    this.moon.color.set(t.keyColor ?? '#8ea6d8');
    // Daylight: no crickets, and only a faint bloom (street lamps just coming on).
    this.audio.setDaytime(!!t.daylight);
    this.post.setBloom(t.daylight ? this.tuning.bloom * 0.35 : this.tuning.bloom);
    this.weather.setKind(level.weather ?? 'clear');
    this.heroine.setUmbrella(level.weather === 'rain');
    this.heroine.setOutfit(level.outfit ?? 'uniform');
    this.dressHeroine(level.look ?? 'school');
    this.footprints.enabled = level.weather === 'snow';
    this.audio.setSnow(level.weather === 'snow');
    this.audio.setWind(level.wind ?? (level.weather === 'snow' ? 0.5 : level.weather === 'rain' ? 0.35 : 0.25));
    this.unease.setNight({ calm: !!level.calm, daylight: (t.daylight ?? 0) > 0.3 });
    this.scares.setNight(level.night, !!level.calm);

    [this.startCell] = this.map.find('S');
    [this.homeCell] = this.map.find('H');
    this.cell = { ...this.startCell };
    this.field = this.map.distanceField(this.startCell.c, this.startCell.r);
    this.homeField = this.map.distanceField(this.homeCell.c, this.homeCell.r);
    this.startHomeDist = Math.max(1, this.homeField[this.map.index(this.startCell.c, this.startCell.r)]);
    this.sctx.map = this.map;
    this.sctx.field = this.field;

    this.stranger.configure(level.stranger);
    this.mom.setScript(level.mom.script);
    this.catEnabled = !!level.cat;
    if (level.cat) {
      const at = ([c, r]: [number, number]) => new THREE.Vector3(c * CELL, 0, r * CELL);
      this.cat.configure(at(level.cat.home), level.cat.path.map(at));
    }
    this.cat.group.visible = this.catEnabled;
    this.hazards.configure(this.map, this.homeField, this.startCell, level.night);
    this.hud.resetPhoneMap();
    this.sendLevelToPhone();
    this.prewarmShaders();
  }

  private sendLevelToPhone(): void {
    this.link.send({ kind: 'level', map: [...this.map.layout] });
  }

  private selectNight(index: number): void {
    if (this.phase !== 'title' || !this.campaign.select(index)) return;
    this.loadLevel(this.campaign.level);
    this.resetRun();
    this.phase = 'title';
    this.startTitleScene();
    this.hud.setHud(false);
    this.hud.setNights(LEVELS, this.campaign.unlocked, this.campaign.current, (i) => this.selectNight(i));
  }

  private nextNight(): void {
    if (!this.campaign.next()) return;
    this.loadLevel(this.campaign.level);
    this.hud.setNights(LEVELS, this.campaign.unlocked, this.campaign.current, (i) => this.selectNight(i));
    this.restart();
  }

  /** The API a level's story hooks get. */
  private createLevelContext(): LevelContext {
    const game = this;
    return {
      get elapsed() {
        return game.elapsed;
      },
      get tension() {
        return game.tension;
      },
      get zone() {
        return game.act;
      },
      get audio() {
        return game.audio;
      },
      get strangerPresent() {
        return game.stranger.isPresent;
      },
      sayOnce: (key) => game.sayOnce(key),
      sayNow: (key) => game.sayNow(key),
      sayLater: (key, delay) => game.pendingLines.push({ at: game.elapsed + delay, key }),
      said: (key) => game.said.has(key),
      spike: (amount) => game.spike(amount),
      notify: (id, from, text, tone = 'normal', delay = 0) => {
        if (game.noted.has(id)) return;
        if (delay > 0) game.pendingNotes.push({ at: game.elapsed + delay, id, from, text, tone });
        else game.sendNote(id, from, text, tone);
      },
      momLater: (node, delay) => game.mom.schedule(node, game.elapsed + delay),
      light: (id) => game.world?.lights.get(id),
      strangerAhead: (c, r) => game.placeStrangerAhead(c, r),
      strangerWatches: (c, r) => game.placeStrangerAhead(c, r, true),
      ring: () => {
        game.audio.ring();
        game.buzz([400, 200, 400, 200, 400, 200, 400]);
      },
      blackout: () => {
        game.world?.blackout(game.heroine.position.x, game.heroine.position.z);
        game.audio.blackout();
        game.spike(0.08);
      },
      silenceMom: () => game.mom.silence(),
    };
  }

  /** Put him standing at a cell ahead of her, if neither he nor that spot is in her view. */
  private placeStrangerAhead(c: number, r: number, watchOnly = false): boolean {
    // Following: he must already be out tonight. Watching: he must not be.
    if (this.strangerVisible || (this.stranger.state === 'waiting') !== watchOnly) return false;
    const p = this.map.center(c, r, new THREE.Vector3());
    p.y = 1.3;
    if (this.isPointVisible(p)) return false;
    this.stranger.appearAhead(p.x, p.z, this.heroine.position, false, watchOnly);
    return true;
  }

  private sendNote(id: string, from: string, text: string, tone: 'normal' | 'system' | 'sent' = 'normal'): void {
    if (this.noted.has(id)) return;
    this.noted.add(id);
    // A photo message carries an actual photo: her, from behind, a moment ago.
    let image: string | undefined;
    if (text.startsWith('[Photo]')) {
      try {
        image = this.takePhotoOfHer().toDataURL('image/jpeg', 0.72);
      } catch (e) {
        console.warn('Photo failed', e);
      }
    }
    this.link.send({ kind: 'notify', from, text, tone, image });
    // Without a linked phone the text slides down on screen, like her lock screen.
    if (!this.link.connected && tone !== 'sent') {
      this.phoneFx.showBanner(from, image ? 'Photo' : text, tone === 'system' ? 'system' : 'normal', image);
      this.audio.textPing();
    }
  }

  private updatePhoneScares(dt: number): void {
    const h = this.heroine;
    const f = (this.scareFrame ??= {
      elapsed: 0,
      night: 1,
      calm: true,
      free: false,
      tension: 0,
      herSpeed: 0,
      lookingBack: false,
      mapOpen: false,
      chatOpen: false,
      flashlight: false,
      dark: false,
      himPresent: false,
      himVisible: false,
      seenHim: false,
      linked: false,
    });
    f.elapsed = this.elapsed;
    f.night = this.level.night;
    f.calm = !!this.level.calm;
    f.free = this.phase === 'playing' && !this.phoneFx.busy && this.elapsed >= this.glanceUntil && this.elapsed >= this.startleUntil;
    f.tension = this.tension;
    f.herSpeed = h.speed;
    f.lookingBack = this.lookingBack;
    f.mapOpen = this.phoneApp === 'map';
    f.chatOpen = this.chatOpen;
    f.flashlight = this.flashlightOn;
    f.dark = this.inDark;
    f.himPresent = this.stranger.isPresent;
    f.himVisible = this.strangerVisible;
    f.seenHim = this.glanceCount > 0 || this.said.has('firstSight');
    f.linked = this.link.connected;
    const action = this.scares.update(f, dt);
    if (action) this.runScare(action);
  }

  private runScare(a: ScareAction): void {
    if (a.kind === 'text') {
      // Only on a linked phone: a text in the player's own hand, looking like their own messages app.
      if (this.link.connected) this.link.send({ kind: 'notify', from: 'Unknown', text: a.text, native: true });
      return;
    }
    if (!this.strangerFace) this.setStrangerFace();
    if (a.kind === 'call') {
      // The call takes over her screen.
      if (this.mom.texting) this.mom.cancelTyping();
      this.chatOpen = false;
      this.phoneLocal = false;
      this.phoneFx.startCall();
      this.audio.callRing(2.7);
      this.buzz([500, 300, 500, 300, 500]);
      this.sendCall('ring');
    } else {
      this.phoneFx.flash();
      this.sendCall('flash');
    }
  }

  /** The call (or map flash) moved on: sound, shake and her reaction to go with it. */
  private onCallStage(stage: CallStage): void {
    const call = this.phoneFx.calling;
    this.hud.phoneScare(stage === 'face');
    if (stage === 'connecting') this.audio.glitch(call ? 0.35 : 0.14, call ? 1 : 0.6);
    else if (stage === 'face') {
      this.audio.jumpScare(call);
      this.buzz([call ? 900 : 250]);
      if (call) this.sendCall('face');
      // A jolt of fear on screen (the chase itself is untouched).
      this.fearVisual = Math.min(1, this.fearVisual + (call ? 0.45 : 0.25));
    } else if (stage === 'ended') {
      this.audio.powerDown();
      this.sendCall('end');
    } else if (stage === 'none' && this.phase === 'playing') {
      this.pendingLines.push({ at: this.elapsed + 0.5, key: this.said.has('flashScare') || this.said.has('callScare') ? 'scareAgain' : this.lastScareWasCall() ? 'callScare' : 'flashScare' });
    }
  }

  private lastScareWasCall(): boolean {
    return this.phoneFx.lastMode === 'call';
  }

  /** Tell a linked phone about the call, so the face comes up in the player's hand too. */
  private sendCall(stage: 'ring' | 'face' | 'flash' | 'end'): void {
    if (!this.link.connected) return;
    if (!this.faceData && this.strangerFace) this.faceData = this.strangerFace.toDataURL('image/jpeg', 0.78);
    this.link.send({ kind: 'call', stage, face: stage === 'ring' || stage === 'flash' ? this.faceData : undefined });
  }

  private setStrangerFace(): void {
    try {
      this.strangerFace = this.renderStrangerFace();
      this.faceData = '';
      this.phoneFx.setFace(this.strangerFace);
    } catch (e) {
      console.warn('Portrait failed', e);
    }
  }

  /**
   * Render `scene` from `cam` into a small canvas, tone-mapped and gamma'd
   * by hand (render targets come back linear), with a little phone-camera grain.
   */
  private snapshot(scene: THREE.Scene, cam: THREE.Camera, exposure: number): HTMLCanvasElement {
    const W = SNAP_W;
    const H = SNAP_H;
    const rt = (this.snapTarget ??= new THREE.WebGLRenderTarget(W, H, { type: THREE.FloatType, depthBuffer: true }));
    const r = this.renderer;
    const prev = r.getRenderTarget();
    r.getClearColor(this.snapColor);
    const prevAlpha = r.getClearAlpha();
    r.setClearColor(0x000000, 1);
    r.setRenderTarget(rt);
    r.clear();
    r.render(scene, cam);
    const px = new Float32Array(W * H * 4);
    r.readRenderTargetPixels(rt, 0, 0, W, H, px);
    r.setRenderTarget(prev);
    r.setClearColor(this.snapColor, prevAlpha);
    const c = document.createElement('canvas');
    c.width = W;
    c.height = H;
    const ctx = c.getContext('2d');
    if (!ctx) return c;
    const img = ctx.createImageData(W, H);
    const map = (v: number): number => {
      const x = Math.max(0, v * exposure);
      const t = (x * (2.51 * x + 0.03)) / (x * (2.43 * x + 0.59) + 0.14);
      const s = t <= 0.0031308 ? t * 12.92 : 1.055 * Math.pow(t, 1 / 2.4) - 0.055;
      return Math.min(255, Math.max(0, s * 255));
    };
    for (let y = 0; y < H; y += 1) {
      const src = (H - 1 - y) * W * 4;
      const dst = y * W * 4;
      for (let x = 0; x < W * 4; x += 4) {
        const grain = (Math.random() - 0.5) * 12;
        img.data[dst + x] = map(px[src + x]) + grain;
        img.data[dst + x + 1] = map(px[src + x + 1]) + grain;
        img.data[dst + x + 2] = map(px[src + x + 2]) + grain;
        img.data[dst + x + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
    return c;
  }

  /** A close, flash-lit picture of his masked face, taken in a little studio of its own. */
  private renderStrangerFace(): HTMLCanvasElement {
    const studio = (this.studio ??= (() => {
      const scene = new THREE.Scene();
      const flash = new THREE.PointLight('#fff1e0', 2.2, 4, 2);
      // A cold edge from behind him, like a streetlight.
      const rim = new THREE.DirectionalLight('#8da6dd', 1.1);
      rim.position.set(0.6, 1.2, 1);
      scene.add(flash, rim, new THREE.AmbientLight('#2a2f3a', 0.35));
      return { scene, flash };
    })());
    const g = this.stranger.group;
    const parent = g.parent;
    const visible = g.visible;
    const pos = this.tmpV.copy(g.position);
    const rotY = g.rotation.y;
    studio.scene.add(g);
    g.position.set(0, 0, 0);
    g.rotation.y = 0;
    g.visible = true;
    g.updateMatrixWorld(true);
    // He faces -z: the camera sits just in front of his face, a little low, tilted.
    const head = this.stranger.headWorld(new THREE.Vector3());
    head.y += FACE_LIFT;
    const cam = this.snapCam;
    cam.fov = 30;
    cam.aspect = SNAP_W / SNAP_H;
    cam.updateProjectionMatrix();
    cam.up.set(0, 1, 0);
    // A little below his eyes, looking up: he is leaning over the lens.
    cam.position.set(head.x + 0.04, head.y - 0.12, head.z - FACE_DIST);
    cam.lookAt(head.x, head.y - 0.04, head.z);
    cam.rotateZ(-0.07);
    cam.updateMatrixWorld(true);
    // His own phone's light, held low.
    studio.flash.position.set(head.x + 0.08, head.y - 0.24, head.z - 0.36);
    let c: HTMLCanvasElement;
    try {
      c = this.snapshot(studio.scene, cam, FACE_EXPOSURE);
    } finally {
      parent?.add(g);
      g.position.copy(pos);
      g.rotation.y = rotY;
      g.visible = visible;
      g.updateMatrixWorld(true);
    }
    return c;
  }

  /** A photo of her from behind, taken from where he is (or a little way back along her path). */
  private takePhotoOfHer(): HTMLCanvasElement {
    const h = this.heroine.position;
    const f = this.heroine.forward;
    const s = this.stranger;
    let x = Number.NaN;
    let z = Number.NaN;
    if (s.isPresent) {
      const dx = s.position.x - h.x;
      const dz = s.position.z - h.z;
      const d = Math.hypot(dx, dz);
      if (d > 3 && d < 26 && dx * f.x + dz * f.z < 0 && this.map.lineOfSight(s.position.x, s.position.z, h.x, h.z)) {
        x = s.position.x;
        z = s.position.z;
      }
    }
    if (Number.isNaN(x)) {
      for (const d of [12, 9, 6, 4]) {
        const px = h.x - f.x * d;
        const pz = h.z - f.z * d;
        if (this.map.isWalkable(px, pz, 0.3) && this.map.lineOfSight(px, pz, h.x, h.z)) {
          x = px;
          z = pz;
          break;
        }
      }
    }
    if (Number.isNaN(x)) {
      x = h.x - f.x * 3;
      z = h.z - f.z * 3;
    }
    const cam = this.snapCam;
    cam.fov = 42;
    cam.aspect = SNAP_W / SNAP_H;
    cam.updateProjectionMatrix();
    cam.up.set(0, 1, 0);
    cam.position.set(x, 1.62, z);
    cam.lookAt(h.x, 1.05, h.z);
    cam.updateMatrixWorld(true);
    // He is the one holding the camera.
    const vis = s.group.visible;
    s.group.visible = false;
    try {
      return this.snapshot(this.scene, cam, PHOTO_EXPOSURE);
    } finally {
      s.group.visible = vis;
    }
  }

  private bindButtons(): void {
    document.querySelector('#begin-button')?.addEventListener('click', () => this.begin());
    document.querySelector('#retry-button')?.addEventListener('click', () => this.restart());
    document.querySelector('#next-button')?.addEventListener('click', () => this.nextNight());
    document.querySelector('#resume-button')?.addEventListener('click', () => this.resume());
    document.querySelector('#restart-button')?.addEventListener('click', () => this.restart());
    document.querySelector('#howto-button')?.addEventListener('click', () => this.setHowTo(true));
    document.querySelector('#howto-close')?.addEventListener('click', () => this.setHowTo(false));
    // Clicking the dimmed backdrop (not the card) closes it too.
    document.querySelector('#howto')?.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) this.setHowTo(false);
    });
    // Desktop (Electron) build: offer a way out without Alt+F4.
    const desktop = window.desktop;
    for (const b of Array.from(document.querySelectorAll<HTMLElement>('.quit-button'))) {
      b.classList.toggle('hidden', !desktop);
      b.addEventListener('click', () => desktop?.quit());
    }
  }

  private setHowTo(open: boolean): void {
    this.howToOpen = open;
    this.hud.showHowTo(open);
    if (!open) {
      // Drop the key that closed it, so the same Enter/Space doesn't also begin the night.
      this.input.read();
      (document.activeElement as HTMLElement | null)?.blur();
    }
  }

  private setupPhoneLink(): void {
    if (!this.link.available) {
      this.hud.setPhoneLink('unavailable');
      return;
    }
    this.hud.setPhoneLink('unavailable');
    if (this.link.kind === 'relay') {
      // Over the relay the phone can be on any network, mobile data included.
      for (const s of Array.from(document.querySelectorAll('.phone-link-text span:not([class])'))) {
        s.textContent = 'Scan it with your phone camera. Your phone becomes hers: her map, her messages, her flashlight.';
      }
    }
    this.link.onConnectionChange = (connected) => {
      this.hud.setPhoneLink(connected ? 'connected' : 'waiting');
      if (connected) this.sendLevelToPhone();
      if (connected && (this.phase === 'playing' || this.phase === 'unlocking')) {
        this.hud.say('My phone. At least I have the map.', 3);
      }
    };
    // Relay builds point the phone at the public phone page (VITE_PHONE_URL, or
    // the one next to this page); the dev server hands out its LAN address.
    const phonePage = (import.meta.env.VITE_PHONE_URL as string | undefined) || new URL('phone.html', location.href).href;
    const urlsReady =
      this.link.kind === 'relay'
        ? Promise.resolve({ urls: [phonePage] })
        : fetch('/__walkhome/lan').then((r) => r.json() as Promise<{ urls: string[] }>);
    void urlsReady
      .then(async ({ urls }) => {
        if (!urls[0]) return;
        // The phone pins itself to this exact game tab.
        const url = `${urls[0]}?game=${this.link.session}`;
        const qr = await QRCode.toDataURL(url, {
          margin: 1,
          width: 264,
          color: { dark: '#e9e4d8', light: '#05070c' },
        });
        this.hud.setPhoneLink(this.link.connected ? 'connected' : 'waiting', url, qr);
      })
      .catch(() => this.hud.setPhoneLink('unavailable'));
  }

  private updateFlashlight(worldLight: number, unlocking: boolean): void {
    // Manual flashlight: on when she switches it on, but only while the map and messages are closed.
    this.flashlightOn = this.flashlightWanted && this.battery > 0 && !unlocking && !this.phoneOpen;
    // In the dark without a light she can barely see where she's going.
    this.inDark = this.inDark ? worldLight < 0.3 : worldLight < 0.18;
    if (this.inDark && !this.flashlightOn && !unlocking && !this.said.has('darkSlow')) {
      this.sayOnce('darkSlow');
      this.darkHintUntil = this.elapsed + 8;
    }
  }

  private toggleFlashlight(): void {
    this.flashlightWanted = !this.flashlightWanted;
    this.audio.click();
    // Switching it on puts the map away (it's the same phone).
    if (this.flashlightWanted && !this.chatOpen) this.phoneLocal = false;
  }

  private updateBattery(dt: number): void {
    if (this.sandboxSafe) this.battery = Math.max(this.battery, 20);
    let drain = DRAIN.standby;
    if (this.flashlightOn) drain += DRAIN.flashlight;
    if (this.phoneOpen) drain += DRAIN.map;
    if (this.mom.texting) drain += DRAIN.texting;
    this.battery = Math.max(0, this.battery - drain * dt);
    this.hud.setBattery(this.battery, this.flashlightOn, this.flashlightWanted);

    for (const [level, key] of [[10, 'batt10'], [5, 'batt5'], [2, 'batt2']] as const) {
      if (this.battery <= level && !this.batteryWarned.has(level)) {
        this.batteryWarned.add(level);
        this.link.send({ kind: 'notify', from: 'Battery', text: `${level}% battery remaining.`, tone: 'system' });
        this.buzz([200]);
        this.sayNow(key, 3.4);
      }
    }
    if (this.battery <= 0 && !this.level.calm) this.startCaught('battery');
  }

  private notify(key: keyof typeof NOTES): void {
    if (this.noted.has(key)) return;
    this.noted.add(key);
    const n: Note = NOTES[key];
    this.link.send({ kind: 'notify', from: n.from, text: n.text, tone: n.tone });
  }

  /** Put the phone away. If Mom is waiting on an answer, she is being ignored. */
  private putAwayPhone(): void {
    // Mid-reply: the unsent message is dropped and Mom is left waiting.
    if (this.mom.texting) this.mom.cancelTyping();
    if (this.chatOpen && this.mom.awaitingReply) this.sayOnce('momPutAway');
    this.chatOpen = false;
    this.phoneLocal = false;
  }

  private buzz(pattern: number[]): void {
    this.link.send({ kind: 'buzz', pattern } satisfies GameEvent);
  }

  private forceResize(): void {
    resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr);
    const dpr = Math.min(window.devicePixelRatio || 1, this.tuning.maxDpr);
    this.post.setSize(Math.max(1, this.canvas.clientWidth), Math.max(1, this.canvas.clientHeight), dpr);
  }

  // ------------------------------------------------------------ flow

  private begin(): void {
    if (this.howToOpen) this.setHowTo(false);
    // Not until she (and he) are loaded: no stand-in figure, no pop-in.
    if (this.phase !== 'title' || !this.ready) return;
    void this.audio.unlock();
    this.hud.showTitle(false);
    this.resetRun();
    this.phase = 'playing';
    this.hud.fade(1, 0);
    requestAnimationFrame(() => this.hud.fade(0, 2.2));
    this.hud.showCard(this.level);
  }

  private restart(): void {
    void this.audio.unlock();
    this.hud.showEnd(null);
    this.hud.showPause(false);
    this.hud.showTitle(false);
    this.resetRun();
    this.phase = 'playing';
    this.audio.setMuted(false);
    this.hud.fade(1, 0);
    requestAnimationFrame(() => this.hud.fade(0, 1.4));
    this.hud.showCard(this.level);
  }

  private pause(): void {
    if (this.phase !== 'playing' && this.phase !== 'unlocking') return;
    this.phase = 'paused';
    this.hud.showPause(true);
    this.audio.setMuted(true);
  }

  private resume(): void {
    if (this.phase !== 'paused') return;
    this.phase = this.unlockProgress > 0 ? 'unlocking' : 'playing';
    this.hud.showPause(false);
    this.audio.setMuted(false);
  }

  private resetRun(): void {
    const start = this.map.center(this.startCell.c, this.startCell.r, this.tmpV);
    this.heroine.reset(start.x, start.z + 2, 0);
    this.heroine.group.visible = true;
    this.stranger.reset();
    this.cat.reset();
    this.cat.group.visible = this.catEnabled;
    this.hazards.reset();
    this.birds.reset();
    this.unease.reset();
    this.scares.reset();
    this.phoneFx.clear();
    this.hud.phoneScare(false);
    this.nearBamboo = this.bambooAround(this.startCell.c, this.startCell.r);
    this.startleUntil = 0;
    this.glanceUntil = 0;
    this.glanceCount = 0;
    this.lastGlanceAt = -99;
    this.glanceReady = true;
    this.hiddenSince = 0;
    this.staggerUntil = 0;
    this.dogBarks = 0;
    this.bumps = 0;
    this.world?.lights.reset();
    this.world?.reset();
    this.map.gatesClosed = false;
    this.crossingState = 'idle';
    this.crossingTime = 0;
    this.endingKey = 'home';
    this.footprints.clear();
    for (const line of this.level.footprints ?? []) {
      this.footprints.trail(line.map(([c, r]) => new THREE.Vector3(c * CELL, 0, r * CELL)));
    }
    this.trail.clear();
    this.trail.record(this.heroine.position);
    this.cell = { ...this.startCell };
    this.field = this.map.distanceField(this.cell.c, this.cell.r, this.field);
    this.sctx.field = this.field;
    this.prevDir = { dc: 0, dr: -1 };
    this.act = zoneFor(this.level, this.startCell.c, this.startCell.r);
    this.tension = 0;
    this.fearVisual = 0;
    this.peakTension = 0;
    this.maxProgress = 0;
    this.elapsed = 0;
    this.lit = true;
    this.inShrine = false;
    this.visitedShrine = false;
    this.phoneOpen = false;
    this.phoneLocal = false;
    this.chatOpen = false;
    this.chatCloseAt = 0;
    this.phoneApp = 'none';
    this.battery = this.level.battery;
    this.flashlightWanted = false;
    this.inDark = false;
    this.darkHintUntil = 0;
    this.flashlightOn = false;
    this.batteryWarned.clear();
    this.torchHintUntil = 0;
    this.caughtCause = 'caught';
    this.deadTextIndex = 0;
    this.deadTextTimer = 0;
    this.hud.setRed(false);
    this.hud.setBattery(this.battery, false, true);
    this.noted.clear();
    this.pendingNotes = [];
    this.mom.reset();
    this.mom.schedule(this.level.mom.first.node, this.level.mom.first.at);
    this.hud.setChat(null);
    this.link.send({ kind: 'reset' });
    this.hud.setPhone(false);
    this.wasLookingBack = false;
    this.strangerVisible = false;
    this.lastSeenAt = -99;
    this.vanishedRecently = false;
    this.spawnTimer = 0;
    this.unlockProgress = 0;
    this.endTime = 0;
    this.endShown = false;
    this.said.clear();
    this.pendingLines = [];
    this.stats = { lookBacks: 0, closest: Infinity, litTime3: 0, darkTime12: 0, stillTime: 0, torchTime3: 0, dogBarks: 0, bumps: 0, unwatchedTime: 0 };
    this.hud.clearLines();
    this.hud.setPrompt(null);
    this.hud.setHud(true);
    this.hud.showEnd(null);
    this.rig.snap(this.heroine.position, this.heroine.heading);
    this.pendingLines.push({ at: 1.4, key: 'intro' });
  }

  // ------------------------------------------------------------ main loop

  private update(dt: number, _elapsed: number): void {
    this.frame += 1;
    if (this.pausedForScreenshot) {
      this.publishDiagnostics();
      return;
    }
    if (resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr)) this.forceResize();
    this.time += this.reducedMotion ? 0 : dt;
    const input = this.input.read();

    switch (this.phase) {
      case 'title':
        // Enter/Space/Esc close "How to play" first; only then does Enter begin.
        if (this.howToOpen) {
          if (input.confirm || input.pause) this.setHowTo(false);
        } else if (input.confirm) this.begin();
        this.hud.setLoading(this.ready ? null : this.loadFraction);
        break;
      case 'playing':
      case 'unlocking':
        if (input.pause) this.pause();
        else if (input.restart) this.restart();
        else this.simulate(dt, input);
        break;
      case 'paused':
        if (input.confirm || input.pause) this.resume();
        else if (input.restart) this.restart();
        break;
      case 'caught':
        this.updateCaught(dt);
        if (this.endShown && (input.restart || input.confirm)) this.restart();
        break;
      case 'home':
        this.endTime += dt;
        if (this.endTime > 1.6 && !this.endShown) this.showHomeEnd();
        if (this.endShown && (input.restart || input.confirm)) this.restart();
        break;
    }

    this.world?.lights.update(dt, this.time, this.heroine.position);
    this.world?.update(dt);
    const hereCh = this.map.char(this.cell.c, this.cell.r);
    const sheltered = 'RYWQ'.includes(hereCh);
    this.weather.update(dt, this.camera.position, sheltered);
    this.heroine.setSheltered(sheltered);
    const rainHere = this.phase === 'title' ? this.weather.rainLevel * 0.5 : this.weather.rainLevel;
    this.audio.setRain(rainHere);
    this.audio.setUmbrellaRain(rainHere * this.heroine.umbrellaOpen);
    this.updateCamera(dt);
    this.updateVisibility();

    const active = this.phase === 'playing' || this.phase === 'unlocking' || this.phase === 'caught';
    const targetFear = active ? this.tension : 0;
    this.fearVisual += (targetFear - this.fearVisual) * Math.min(1, dt * 1.5);
    const beatHz = 1 / (1.15 - this.tension * 0.75);
    const pulse = active
      ? Math.pow(Math.max(0, Math.sin(this.time * Math.PI * 2 * beatHz)), 14) * THREE.MathUtils.smoothstep(this.tension, 0.3, 1)
      : 0;
    this.post.update(this.time, this.fearVisual, pulse);
    this.post.setFocus(this.camera.position.distanceTo(this.heroine.group.position));
    this.heroine.setFear(this.fearVisual);

    let hum = 0;
    for (const v of this.world?.vendingSpots ?? []) {
      const d = Math.hypot(v.x - this.heroine.position.x, v.z - this.heroine.position.z);
      hum = Math.max(hum, THREE.MathUtils.clamp(1 - d / 7, 0, 1));
    }
    const panic = this.tension > 0.7 ? (this.tension - 0.7) * 2.5 : 0;
    this.audio.update(dt, {
      tension: this.tension,
      active: this.phase === 'playing' || this.phase === 'unlocking',
      breath: active ? Math.min(1, panic) : 0,
      hum: this.phase === 'title' ? 0 : hum,
    });

    const hd = this.homeSteps();
    this.link.update({
      x: this.heroine.position.x,
      z: this.heroine.position.z,
      heading: this.heroine.heading,
      homeMeters: Math.max(0, hd) * CELL,
      clock: this.clockText(),
      phase: this.phase,
      tension: this.tension,
      open: this.phoneOpen,
      levelId: this.level.id,
      battery: Math.max(0, Math.ceil(this.battery)),
      flashlight: this.flashlightOn,
      app: this.phoneApp,
      mom: this.phase === 'playing' || this.phase === 'unlocking' ? this.mom.view() : null,
      route: this.phase === 'playing' || this.phase === 'unlocking' ? this.directions.view(this.time) : null,
    });
    this.hud.setChat(this.phase === 'playing' || this.phase === 'unlocking' ? this.mom.view() : null);

    this.hud.update(dt);
    const callStage = this.phoneFx.update(this.phase === 'paused' ? 0 : dt);
    if (callStage) this.onCallStage(callStage);
    this.updateDebug();
    this.publishDiagnostics();
  }

  private render(): void {
    this.renderer.info.reset();
    this.post.render();
  }

  private simulate(dt: number, input: InputFrame): void {
    const h = this.heroine;
    const unlocking = this.phase === 'unlocking';

    if (input.phoneToggle && !unlocking) {
      if (this.chatOpen) this.putAwayPhone();
      else this.phoneLocal = !this.phoneLocal;
      this.audio.phoneTap(this.phoneLocal);
    }
    if (input.reply >= 0 && !unlocking && this.mom.awaitingReply) {
      this.chatOpen = true;
      this.mom.reply(input.reply);
    }
    if (!unlocking && !this.sandboxQuiet) this.mom.update(this.elapsed, dt);
    if (this.chatCloseAt && this.elapsed >= this.chatCloseAt) {
      this.chatOpen = false;
      this.chatCloseAt = 0;
    }
    // Messages take over the screen; otherwise holding the real phone (or Tab) shows the map.
    this.phoneApp = unlocking ? 'none' : this.chatOpen ? 'chat' : this.phoneLocal || this.link.remoteHold ? 'map' : 'none';
    this.phoneOpen = this.phoneApp !== 'none';
    this.hud.setPhoneApp(this.phoneApp, this.link.connected, this.phoneFx.calling);
    if (!unlocking) h.shiftGait(input.gaitDelta);
    this.rig.addLook(input.lookX, input.lookY);
    const glancing = this.elapsed < this.glanceUntil && !unlocking;
    // Turning to look at him counts as looking back when he is behind her.
    const glanceBehind = glancing && this.isBehindHer(this.glanceAt);
    this.lookingBack = (input.lookBack || glanceBehind) && !unlocking;
    if (this.lookingBack && !this.wasLookingBack) this.stats.lookBacks += 1;
    this.wasLookingBack = this.lookingBack;

    if (input.flashlightToggle && !unlocking) this.toggleFlashlight();
    const texting = this.mom.texting && !unlocking;

    if (unlocking) h.heading += wrapAngle(0 - h.heading) * Math.min(1, dt * 5);
    const startled = this.elapsed < this.startleUntil;
    let headYaw = this.lookingBack ? (this.rig.yawOffset >= 0 ? 1.25 : -1.25) : this.rig.yawOffset * 0.7;
    if (startled) {
      // Head snaps toward the barking dog.
      const toDog = Math.atan2(-(this.startleAt.x - h.position.x), -(this.startleAt.z - h.position.z));
      headYaw = THREE.MathUtils.clamp(wrapAngle(toDog - h.heading), -1.25, 1.25);
    } else if (glancing) {
      // ...or turns toward him.
      const toHim = Math.atan2(-(this.glanceAt.x - h.position.x), -(this.glanceAt.z - h.position.z));
      headYaw = THREE.MathUtils.clamp(wrapAngle(toHim - h.heading), -1.25, 1.25);
    }
    h.update(
      dt,
      {
        steer: unlocking ? 0 : input.steer,
        lookBack: this.lookingBack,
        phone: this.phoneOpen || texting,
        texting,
        flashlight: this.flashlightOn,
        darkSlow: this.inDark && !this.flashlightOn,
        maxSpeed: this.elapsed < this.staggerUntil ? 0.75 : undefined,
        headYaw,
      },
      this.map,
      unlocking || startled,
    );
    this.trail.record(h.position);
    this.elapsed += dt;

    // Cell transitions drive path-finding, junction logic and story triggers.
    const cell = this.map.cellAt(h.position.x, h.position.z);
    if (cell.c !== this.cell.c || cell.r !== this.cell.r) {
      const prev = this.cell;
      this.cell = cell;
      this.onCellChange(prev, cell);
      this.nearBamboo = this.bambooAround(cell.c, cell.r);
    }

    // Light / dark
    const worldLight = Math.max(this.world?.lights.lightLevelAt(h.position.x, h.position.z) ?? 0, this.level.theme.daylight ?? 0);
    this.lightLevel = worldLight + (this.phoneOpen || this.mom.texting ? 0.14 : 0);
    this.updateFlashlight(worldLight, unlocking);
    this.updateBattery(dt);
    if (this.phase !== 'playing' && this.phase !== 'unlocking') return;
    if (this.lit && this.lightLevel < 0.22) this.lit = false;
    else if (!this.lit && this.lightLevel > 0.3) this.lit = true;

    this.updateTension(dt);
    // Eyes adjust to the dark: a faint fill keeps the path readable.
    this.fill.intensity = 0.3 + THREE.MathUtils.clamp(1 - this.lightLevel * 2, 0, 1) * 0.9;

    // The stranger leaves the station a few seconds after she does.
    if (this.stranger.state === 'waiting' && !this.level.calm) {
      this.spawnTimer += dt;
      const [exit] = this.map.find('P');
      const at = this.level.strangerStart;
      if (at && this.spawnTimer > this.level.stranger.spawnDelay) {
        // Already there, waiting for her.
        this.stranger.appearAhead(at[0] * CELL, at[1] * CELL, this.map.center(this.homeCell.c, this.homeCell.r, this.tmpV), true);
      } else if (exit && this.spawnTimer > this.level.stranger.spawnDelay) {
        const p = this.map.center(exit.c, exit.r, this.tmpV);
        this.stranger.spawn(p.x, p.z + 2, 0);
      }
    }

    const s = this.sctx;
    s.dt = dt;
    s.tension = this.tension;
    s.act = this.act;
    s.playerSpeed = h.speed;
    s.playerLit = this.lit;
    s.flashlight = this.flashlightOn;
    s.playerInShrine = this.inShrine;
    s.lookingBack = this.lookingBack;
    s.visible = this.strangerVisible;
    this.stranger.update(s);
    this.handleStrangerEvents();
    this.updateUnease(dt);
    this.updatePhoneScares(dt);
    this.updateCrossing(dt);
    this.footprints.track('her', h.position.x, h.position.z);
    if (this.stranger.isPresent) this.footprints.track('him', this.stranger.position.x, this.stranger.position.z, 1.25);

    // Streetlights ('F') that die as she approaches.
    for (const light of this.world?.lights.sources ?? []) {
      if (!light.id.startsWith('F-') || light.state !== 'on') continue;
      const d = Math.hypot(light.lamp.x - h.position.x, light.lamp.z - h.position.z);
      if (d < 13) {
        light.setState('dying');
        this.audio.flicker();
        this.spike(0.04);
        this.sayOnce('flicker');
      }
    }

    // Shops ('Q') pull their shutters down as she passes: the arcade gets darker behind her.
    for (const light of this.world?.lights.sources ?? []) {
      if (!light.id.startsWith('Q-') || light.state !== 'on') continue;
      if (Math.hypot(light.lamp.x - h.position.x, light.lamp.z - h.position.z) < 6.5) {
        light.setState('off');
        this.world?.closeShutter(light.id);
        this.audio.shutter();
        this.sayOnce('shutter');
      }
    }

    // In a traffic mirror, for a second: him.
    this.mirrorCooldown -= dt;
    if (this.ghost && this.elapsed > this.ghostUntil) {
      this.ghost.visible = false;
      this.ghost = null;
    }
    if (this.level.mirrorSightings && this.mirrorCooldown <= 0 && this.stranger.isPresent) {
      const sdx = this.stranger.position.x - h.position.x;
      const sdz = this.stranger.position.z - h.position.z;
      const behind = sdx * h.forward.x + sdz * h.forward.z < 0;
      const near = Math.hypot(sdx, sdz) < 32;
      for (const m of this.world?.mirrors ?? []) {
        const d = Math.hypot(m.pos.x - h.position.x, m.pos.z - h.position.z);
        if (d > 7 || !behind || !near) continue;
        m.ghost.visible = true;
        this.ghost = m.ghost;
        this.ghostUntil = this.elapsed + 1.4;
        this.mirrorCooldown = 25;
        this.spike(0.03);
        this.sayOnce('mirror');
        break;
      }
    }

    this.updateHazards(dt);
    this.birds.update(dt, h.position, this.stranger.isPresent ? this.stranger.position : null, h.speed > 2.2);

    if (this.catEnabled && this.cat.update(dt, h.position)) {
      this.audio.meow();
      if (this.level.cat?.line) this.sayOnce(this.level.cat.line);
    }

    if (unlocking) {
      this.unlockProgress += dt / 2.4;
      this.hud.setPrompt(this.endingKey === 'home' ? 'Unlocking' : (this.level.altGoal?.prompt ?? 'Inside'), this.unlockProgress);
      if (this.unlockProgress >= 1) this.finishHome();
    }

    if (this.stranger.isPresent) {
      const d = Math.hypot(this.stranger.position.x - h.position.x, this.stranger.position.z - h.position.z);
      this.stats.closest = Math.min(this.stats.closest, d);
    }
    // finishHome() may have run above this frame, so re-read the phase.
    if (this.stranger.events.caught && (this.phase as Phase) !== 'home') this.startCaught('caught');

    // Pacing feedback
    this.hud.setPace(h.gaitLabel, h.gait);
    // Directions update even with the phone away: she still sees barriers, and the app catches up.
    this.directions.update(this.time, this.cell.c, this.cell.r);
    if (this.phoneOpen) {
      const hd = this.homeSteps();
      const route = this.directions.view(this.time);
      this.hud.drawPhone(this.map, h.position.x, h.position.z, h.heading, Math.max(0, hd) * CELL, this.clockText(), this.time, route);
    }
    // In act 3 her flashlight lets him follow: say so once, with the key.
    if (this.act === 3 && this.flashlightOn && !this.said.has('torchAct3')) {
      this.sayOnce('torchAct3');
      this.torchHintUntil = this.elapsed + 8;
    }
    // She hasn't looked back for a while: he's creeping up.
    const creep = this.stranger.creep;
    if (creep > 0.2 && this.elapsed - this.unwatchedSaidAt > 40) {
      this.sayNow(this.said.has('unwatched') ? 'unwatched2' : 'unwatched', 3);
      if (!this.said.has('unwatchedHint')) {
        this.said.add('unwatchedHint');
        this.unwatchedHintUntil = this.elapsed + 7;
      }
      this.unwatchedSaidAt = this.elapsed;
    }
    this.hud.setHint(
      this.elapsed > 1 && this.elapsed < 17
        ? '<kbd>W</kbd><kbd>S</kbd> pace &nbsp;·&nbsp; <kbd>A</kbd><kbd>D</kbd> turn &nbsp;·&nbsp; <kbd>Q</kbd> look back &nbsp;·&nbsp; <kbd>Tab</kbd> map &nbsp;·&nbsp; <kbd>F</kbd> flashlight'
        : this.elapsed < this.unwatchedHintUntil && creep > 0
          ? '<kbd>Q</kbd> look back: keep checking on him, or he creeps closer'
          : this.elapsed < this.torchHintUntil && this.flashlightOn
          ? '<kbd>F</kbd> switch the flashlight off'
          : this.elapsed < this.darkHintUntil && this.inDark && !this.flashlightOn
            ? '<kbd>F</kbd> flashlight: she walks slowly in the dark without it'
            : null,
    );

    // Tension-driven thoughts
    if (this.tension > 0.45) this.sayOnce('t45');
    if (this.tension > 0.7) this.sayOnce('t70');
    if (this.tension >= 0.92 && !this.said.has('t92')) {
      this.said.add('t92');
      this.sayNow('t92', 3);
    }
    if (this.stats.stillTime > 6) this.sayOnce('still');
    for (let i = this.pendingNotes.length - 1; i >= 0; i -= 1) {
      if (this.elapsed >= this.pendingNotes[i].at) {
        const n = this.pendingNotes.splice(i, 1)[0];
        this.sendNote(n.id, n.from, n.text, n.tone);
      }
    }
    for (let i = this.pendingLines.length - 1; i >= 0; i -= 1) {
      const pl = this.pendingLines[i];
      if (this.elapsed >= pl.at) {
        this.pendingLines.splice(i, 1);
        this.sayOnce(pl.key);
      }
    }
  }

  private onCellChange(prev: CellRef, cell: CellRef): void {
    const map = this.map;
    this.field = map.distanceField(cell.c, cell.r, this.field);
    this.sctx.field = this.field;

    const dir = { dc: cell.c - prev.c, dr: cell.r - prev.r };
    if (Math.abs(dir.dc) + Math.abs(dir.dr) === 1) {
      if (this.prevDir && (this.prevDir.dc !== dir.dc || this.prevDir.dr !== dir.dr) && map.openNeighbours(prev.c, prev.r) >= 3) {
        this.stranger.tryDecoy(this.sctx);
      }
      this.prevDir = dir;
    }

    const act = zoneFor(this.level, cell.c, cell.r);
    if (act !== this.act) {
      this.act = act;
      this.level.onZoneChange?.(this.ctx, act);
    }

    // Quiet paths ('p'): dark, but he won't follow her in.
    const wasInShrine = this.inShrine;
    this.inShrine = map.isShrine(cell.c, cell.r);
    if (this.inShrine && !wasInShrine) {
      this.visitedShrine = true;
      this.sayOnce('path');
    }
    if (!this.inShrine && wasInShrine && this.visitedShrine && !this.stranger.isPresent) this.sayOnce('pathExitGone');

    const ch = map.char(cell.c, cell.r);
    // A bright entrance that isn't hers.
    if (ch === 'A' && !this.said.has('wrongBuilding')) {
      this.spike(0.04);
      this.sayOnce('wrongBuilding');
    }
    this.level.onEnterCell?.(this.ctx, cell, prev, ch);

    // The sensor light snaps on just after she walks past it.
    if (map.char(prev.c, prev.r) === 'T') {
      const sensor = this.world?.lights.get('sensor');
      if (sensor && sensor.state === 'sensorOff') {
        sensor.setState('sensorOn');
        this.audio.sensorClick();
        this.spike(0.08);
        this.sayNow('sensor', 3.8);
      }
    }

    const alt = this.level.altGoal;
    const altHere = !!alt && cell.c === alt.cell[0] && cell.r === alt.cell[1];
    if ((ch === 'H' || altHere) && this.phase === 'playing') {
      this.endingKey = altHere && alt ? alt.ending : 'home';
      this.phase = 'unlocking';
      this.unlockProgress = 0;
      this.phoneOpen = false;
      this.phoneLocal = false;
      this.chatOpen = false;
    this.chatOpen = false;
    this.chatCloseAt = 0;
    this.phoneApp = 'none';
      this.hud.setPhone(false);
      this.audio.keys();
      this.sayNow('unlock', 2.4);
    }
  }

  private updateTension(dt: number): void {
    if (this.level.calm) {
      this.tension = 0;
      return;
    }
    const h = this.heroine;
    let rate = 0.0028;
    if (h.speed < 0.35 && this.phase === 'playing') {
      rate += 0.005;
      this.stats.stillTime += dt;
    }
    if (this.phoneOpen || this.mom.texting) rate += 0.003;
    // The flashlight takes the edge off the dark early on; near home it lets him follow her.
    const torch = this.flashlightOn;
    if (this.act === 1) rate += this.lit ? -0.0045 : torch ? 0.0035 : 0.0075;
    else if (this.act === 2) rate += this.inShrine ? -0.012 : this.lit ? -0.0025 : torch ? 0.002 : 0.0045;
    else rate += this.lit ? 0.0095 : torch ? 0.003 : -0.0025;
    // Not checking behind her lets the dread build.
    const creep = this.stranger.creep;
    if (creep > 0) {
      rate += creep * 0.004;
      this.stats.unwatchedTime += dt;
    }
    if (this.act === 3 && torch && !this.lit) this.stats.torchTime3 += dt;
    if (this.act < 3 && !this.lit && !this.inShrine) this.stats.darkTime12 += dt;
    if (this.act === 3 && this.lit) this.stats.litTime3 += dt;

    this.tension += rate * dt * this.tuning.tensionScale * (this.level.pace?.tension ?? 1);
    const hd = this.homeField[this.map.index(this.cell.c, this.cell.r)];
    if (hd >= 0) this.maxProgress = Math.max(this.maxProgress, 1 - hd / this.startHomeDist);
    // Dawdling is never free: the floor rises with time (longer nights allow longer).
    const floor = Math.max(this.maxProgress * 0.5, this.elapsed / (this.level.pace?.patience ?? 420));
    this.tension = THREE.MathUtils.clamp(this.tension, Math.min(floor, 0.97), 1);
    this.peakTension = Math.max(this.peakTension, this.tension);
  }

  /** Dogs and passers-by: they don't hurt her, they just cost her time. */
  private updateHazards(dt: number): void {
    const h = this.heroine;
    const ev = this.hazards.update({
      dt,
      her: h.position,
      forward: h.forward,
      speed: h.speed,
      gait: h.gait,
      time: this.time,
      stranger: this.stranger.isPresent ? this.stranger.position : null,
      homeDist: Math.max(0, this.homeField[this.map.index(this.cell.c, this.cell.r)]),
    });
    if (ev.dogSeen) this.sayOnce('dogSleeping');
    if (ev.bark) {
      const dx = ev.bark.x - h.position.x;
      const dz = ev.bark.z - h.position.z;
      const d = Math.hypot(dx, dz);
      const camRight = this.tmpV.set(1, 0, 0).applyQuaternion(this.camera.quaternion);
      const pan = d > 0.01 ? (dx * camRight.x + dz * camRight.z) / d : 0;
      this.audio.bark(THREE.MathUtils.clamp(1.2 - d / 30, 0.15, 1), pan * 0.8);
    }
    if (ev.wake) {
      this.audio.growl(0.8);
      this.startleAt.copy(ev.wake);
      // Rooted to the spot for a moment: the stranger doesn't stop.
      this.startleUntil = this.elapsed + 1.3;
      this.dogBarks += 1;
      this.stats.dogBarks += 1;
      this.spike(0.03);
      this.buzz([90, 40, 90]);
      this.sayNow(this.dogBarks === 1 ? 'dogBark' : 'dogBark2', 2.6);
    }
    if (ev.bump) {
      const p = h.position;
      const push = 0.55;
      if (this.map.isWalkable(p.x + ev.bump.x * push, p.z + ev.bump.z * push)) {
        p.x += ev.bump.x * push;
        p.z += ev.bump.z * push;
      }
      h.speed = 0;
      h.heading = wrapAngle(h.heading + (this.rng() < 0.5 ? -0.3 : 0.3));
      this.staggerUntil = this.elapsed + 2.4;
      this.bumps += 1;
      this.stats.bumps += 1;
      this.audio.bump();
      this.spike(0.015);
      this.buzz([70]);
      this.sayNow(this.bumps === 1 ? 'bump' : 'bump2', 2.4);
    }
  }

  private spike(amount: number): void {
    this.buzz([60 + Math.round(amount * 600)]);
    this.tension = Math.min(1, this.tension + amount * this.tuning.tensionScale);
  }

  /**
   * The level crossing: when she gets near, the bell starts, the gates come
   * down and a train goes through. She has to wait it out (he doesn't).
   */
  private updateCrossing(dt: number): void {
    const crossing = this.world?.crossing;
    if (!crossing) return;
    const h = this.heroine.position;
    let nearest = Infinity;
    let onCrossing = false;
    for (const k of crossing.cells) {
      const d = Math.hypot((k.c + 0.5) * CELL - h.x, (k.r + 0.5) * CELL - h.z);
      nearest = Math.min(nearest, d);
      if (k.c === this.cell.c && k.r === this.cell.r) onCrossing = true;
    }
    this.crossingTime += dt;
    switch (this.crossingState) {
      case 'idle':
        if (nearest < 22 && !onCrossing) {
          this.crossingState = 'warn';
          this.crossingTime = 0;
          crossing.setWarning(true);
          this.sayOnce('crossing');
        }
        break;
      case 'warn':
        // Nobody gets shut in on the tracks: the gates wait for her to step off.
        if (this.crossingTime > 3.2 && !onCrossing) {
          this.crossingState = 'closed';
          this.crossingTime = 0;
          this.map.gatesClosed = true;
          crossing.setGates(true);
          crossing.runTrain();
          this.audio.trainPass(11);
        }
        break;
      case 'closed':
        if (this.crossingTime > 2 && nearest < 14) this.sayOnce('crossingWait');
        if (crossing.train === 'gone' && this.crossingTime > 6) {
          this.crossingState = 'cooldown';
          this.crossingTime = 0;
          this.map.gatesClosed = false;
          crossing.setGates(false);
          crossing.setWarning(false);
        }
        break;
      case 'cooldown':
        if (this.crossingTime > 30 && nearest > 26) this.crossingState = 'idle';
        break;
    }
    if (this.crossingState === 'warn' || this.crossingState === 'closed') {
      this.bellTimer -= dt;
      if (this.bellTimer <= 0) {
        this.bellTimer = 0.44;
        this.audio.crossingBell(THREE.MathUtils.clamp(1 - nearest / 70, 0.05, 1));
      }
    }
  }

  private handleStrangerEvents(): void {
    const ev = this.stranger.events;
    this.updateGlance(ev.noticed);
    if (this.strangerVisible) {
      this.sayOnce('firstSight');
      if (this.vanishedRecently && this.elapsed - this.lastSeenAt > 2) {
        this.vanishedRecently = false;
        this.sayOnce('back');
      }
      this.lastSeenAt = this.elapsed;
    }
    if (ev.stoppedWithHer) this.sayOnce('stopped');
    if (ev.phonePose) this.sayOnce('phone');
    if (ev.waitedAtShrine) this.sayOnce('pathWait');
    if (ev.noticed) {
      this.spike(0.12);
      this.sayNow('noticed', 3.4);
    }
    if (ev.vanished && this.elapsed - this.lastSeenAt < 3 && !this.inShrine) {
      this.vanishedRecently = true;
      this.pendingLines.push({ at: this.elapsed + 1.5, key: 'vanished' });
    }
  }

  /**
   * She notices him: her head turns, and the camera swings over her shoulder
   * and zooms in on him for a moment. The first time he is plainly there each
   * night (close, or lit, with nothing in the way), when he turns up again
   * after being gone a while, and when he spots her waiting at her building.
   */
  private updateGlance(spottedHer: boolean): void {
    const s = this.stranger;
    if (!s.isPresent) {
      this.glanceUntil = Math.min(this.glanceUntil, this.elapsed);
      if (this.hiddenSince === 0) this.hiddenSince = this.elapsed;
      // Gone long enough that seeing him again is a shock.
      if (this.elapsed - this.hiddenSince > GLANCE_REARM) this.glanceReady = true;
      return;
    }
    this.hiddenSince = 0;
    if (this.elapsed < this.glanceUntil) {
      // Cut it short if he slips out of sight.
      if (!this.canSeeHim()) this.glanceUntil = Math.min(this.glanceUntil, this.elapsed + 0.3);
      return;
    }
    const due = spottedHer || this.glanceReady;
    if (!due || this.glanceCount >= GLANCE_MAX || this.elapsed - this.lastGlanceAt < GLANCE_COOLDOWN) return;
    // Not over the phone, the unlock, or the very start of the walk.
    if (this.phase !== 'playing' || this.phoneOpen || this.mom.texting || this.elapsed < 3) return;
    const d = Math.hypot(s.position.x - this.heroine.position.x, s.position.z - this.heroine.position.z);
    const lit = (this.world?.lights.lightLevelAt(s.position.x, s.position.z) ?? 0) > 0.2 || (this.level.theme.daylight ?? 0) > 0.3;
    if (d > GLANCE_FAR || (d > GLANCE_NEAR && !lit) || !this.canSeeHim()) return;
    this.glanceReady = false;
    this.glanceCount += 1;
    this.lastGlanceAt = this.elapsed;
    this.glanceUntil = this.elapsed + GLANCE_TIME;
    this.glanceAt.copy(s.position).setY(GLANCE_HEIGHT);
    this.spike(0.06);
    if (this.glanceCount === 1) this.sayOnce('firstSight');
  }

  private updateUnease(dt: number): void {
    const h = this.heroine;
    const f = (this.uframe ??= {
      dt: 0,
      elapsed: 0,
      tension: 0,
      her: h.position,
      herForward: h.forward,
      herSpeed: 0,
      lookingBack: false,
      him: null,
      himVisible: false,
      nearBamboo: 0,
      camera: this.camera,
      map: this.map,
      lights: [],
      lightLevelAt: (x, z) => this.world?.lights.lightLevelAt(x, z) ?? 0,
      isVisible: (p) => this.isPointVisible(p),
    });
    f.dt = dt;
    f.elapsed = this.elapsed;
    f.tension = this.tension;
    f.herSpeed = h.speed;
    f.lookingBack = this.lookingBack;
    f.him = this.stranger.isPresent ? this.stranger.position : null;
    f.himVisible = this.strangerVisible;
    f.nearBamboo = this.nearBamboo;
    f.map = this.map;
    f.lights = this.world?.lights.sources ?? [];
    this.unease.update(f);
  }

  /** Share of the cells around (c, r) that are bamboo. */
  private bambooAround(c: number, r: number): number {
    let n = 0;
    for (let dr = -1; dr <= 1; dr += 1) {
      for (let dc = -1; dc <= 1; dc += 1) if (this.map.char(c + dc, r + dr) === 'j') n += 1;
    }
    return Math.min(1, n / 3);
  }

  private canSeeHim(): boolean {
    const h = this.heroine.position;
    const p = this.stranger.position;
    return this.map.lineOfSight(h.x, h.z, p.x, p.z);
  }

  private isBehindHer(p: THREE.Vector3): boolean {
    const h = this.heroine;
    return (p.x - h.position.x) * h.forward.x + (p.z - h.position.z) * h.forward.z < 0;
  }

  private lineFor(key: string): string | undefined {
    return this.level.lines[key] ?? BASE_LINES[key];
  }

  /** Say a line once per night (silently skipped if this night has no such line). */
  private sayOnce(key: string): void {
    if (this.said.has(key)) return;
    this.said.add(key);
    const line = this.lineFor(key);
    if (line) this.hud.say(line);
  }

  /** Say a line right now, interrupting whatever she was thinking. */
  private sayNow(key: string, duration = 3.4): void {
    this.said.add(key);
    const line = this.lineFor(key);
    if (line) this.hud.say(line, duration, true);
  }

  private strangerFootstep(running: boolean): void {
    if (this.phase !== 'playing' && this.phase !== 'unlocking') return;
    const s = this.stranger.position;
    const h = this.heroine.position;
    const dx = s.x - h.x;
    const dz = s.z - h.z;
    const d = Math.hypot(dx, dz);
    // Rain drowns out his footsteps (but under the arcade roof they echo).
    const vol = Math.pow(THREE.MathUtils.clamp(1 - d / 40, 0, 1), 1.3) * (1 - this.weather.rainLevel * 0.75);
    // Pan relative to the camera's right vector.
    const camRight = this.tmpV.set(1, 0, 0).applyQuaternion(this.camera.quaternion);
    const pan = d > 0.01 ? (dx * camRight.x + dz * camRight.z) / d : 0;
    this.audio.footstepHim(vol, pan * 0.8, running);
  }

  // ------------------------------------------------------------ endings

  private startCaught(cause: 'caught' | 'battery'): void {
    if (this.sandboxSafe) return;
    this.phoneFx.clear();
    this.hud.phoneScare(false);
    this.phase = 'caught';
    this.caughtCause = cause;
    this.flashlightOn = false;
    this.heroine.killFlashlight();
    this.hud.setBattery(this.battery, false, false);
    this.endTime = 0;
    this.endShown = false;
    this.phoneOpen = false;
    this.hud.setPhone(false);
    this.hud.setPrompt(null);
    this.hud.setHint(null);
    this.hud.clearLines();
    if (cause === 'battery') {
      this.audio.powerDown();
      this.buzz([900]);
    } else {
      this.audio.sting();
      this.buzz([500, 120, 800]);
      this.notify('momLate');
    }
    this.hud.setChat(null);
    this.phoneLocal = false;
    this.chatOpen = false;
    this.chatCloseAt = 0;
    this.phoneApp = 'none';
    // Put him right behind her, reaching out.
    const h = this.heroine;
    const back = this.tmpV.set(Math.sin(h.heading), 0, Math.cos(h.heading));
    this.stranger.position.set(h.position.x + back.x * 1.0, 0, h.position.z + back.z * 1.0);
    this.stranger.heading = h.heading;
    this.stranger.group.rotation.y = h.heading;
    this.stranger.group.visible = true;
  }

  private updateCaught(dt: number): void {
    this.endTime += dt;
    this.stranger.reachOut(dt, this.endTime);
    if (this.endTime - dt <= 0.25 && this.endTime > 0.25) this.heroine.flinch();
    this.heroine.animateIdle(dt, Math.min(1.3, this.endTime * 2));
    const battery = this.caughtCause === 'battery';
    if (battery) {
      // The street goes red as he grabs her; her phone keeps lighting up with Mom's texts.
      if (this.endTime > 0.35 && this.endTime - dt <= 0.35) {
        this.hud.setRed(true);
        this.audio.sting();
      }
      this.deadTextTimer -= dt;
      if (this.endTime > 1.4 && this.deadTextTimer <= 0) {
        this.deadTextTimer = 2.8;
        const text = DEAD_PHONE_TEXTS[this.deadTextIndex];
        this.deadTextIndex = this.deadTextIndex + 1 < DEAD_PHONE_TEXTS.length ? this.deadTextIndex + 1 : 4;
        this.link.send({ kind: 'notify', from: MOM_NAME, text });
        this.buzz([60, 90, 60]);
        if (this.endShown) this.hud.addEndText(MOM_NAME, text);
      }
    }
    if (this.endTime > (battery ? 1.6 : 0.9) && this.endTime - dt <= (battery ? 1.6 : 0.9)) this.hud.fade(battery ? 0.55 : 1, 1.6);
    if (this.endTime > 2.8 && !this.endShown) {
      this.endShown = true;
      this.hud.setHud(false);
      this.hud.showEnd({
        title: battery ? 'Her phone died.' : 'She didn\'t make it home.',
        lines: battery
          ? ['The screen went black. So did the street.', 'A hand closed around her wrist.']
          : ['A hand on her shoulder.', 'A voice, much too close: “Excuse me…”'],
        hint: battery
          ? 'The flashlight drains her battery fast. Use it where you need it, and switch it off (F) in the last stretch.'
          : this.caughtHint(),
        stats: [
          ['Time walked', this.formatTime(this.elapsed)],
          ['Looked back', `${this.stats.lookBacks}×`],
        ],
        win: false,
      });
      // Texts that arrived before the end screen opened.
      if (battery) for (let i = 0; i < this.deadTextIndex; i += 1) this.hud.addEndText(MOM_NAME, DEAD_PHONE_TEXTS[i]);
    }
  }

  private caughtHint(): string {
    const s = this.stats;
    const custom = this.level.caughtHint?.(this.ctx);
    if (custom) return custom;
    if (s.unwatchedTime > 15) return 'She stopped checking behind her, and he crept up. Look back (Q) every few seconds.';
    if (this.act === 3 && s.torchTime3 > 8) return 'He followed her flashlight all the way. In the dark he loses track of her: switch it off (F).';
    if (this.act === 3 && s.litTime3 > 10) return 'Under the streetlights, he could always see her. Near home, the dark was safer.';
    if (this.mom.ignoredTotal >= 2) return 'Every text she ignored, he closed the distance. Answer Mom, even if it slows you down.';
    if (s.dogBarks >= 2) return 'Barking dogs froze her on the spot. Walk slowly past a sleeping dog, on the far side of the street.';
    if (s.bumps >= 2) return 'People in a hurry won\'t move for her. Step aside (A / D) before they reach her.';
    if (s.stillTime > 15) return 'Every time she stopped, he came a little closer.';
    if (this.elapsed > (this.level.pace?.patience ?? 420) * 0.6) return 'She took too long. Wrong turns cost more than you think.';
    if (s.darkTime12 > 25) return 'Early on, the dark stretches let him close in. Stay in the light — at first.';
    return 'Light isn\'t always safe. Darkness isn\'t always dangerous.';
  }

  private finishHome(): void {
    this.phoneFx.clear();
    this.hud.phoneScare(false);
    this.phase = 'home';
    this.campaign.complete(this.endingKey);
    this.hud.setNights(LEVELS, this.campaign.unlocked, this.campaign.current, (i) => this.selectNight(i));
    this.endTime = 0;
    this.endShown = false;
    this.hud.setPrompt(null);
    this.hud.setHint(null);
    this.audio.doorLock();
    this.buzz([30, 70, 30]);
    this.notify('home');
    this.hud.setChat(null);
    this.hud.fade(1, 1.2);
  }

  private showHomeEnd(): void {
    this.endShown = true;
    this.hud.setHud(false);
    this.hud.clearLines();
    const peak = this.peakTension;
    const feeling = peak < 0.4 ? 'Uneasy' : peak < 0.65 ? 'Afraid' : peak < 0.9 ? 'Terrified' : 'Hunted';
    const custom = this.level.endings?.[this.endingKey];
    this.hud.showEnd({
      title: custom?.title ?? 'She\'s home.',
      lines: custom?.lines ?? [
        'The lock clicks. The chain slides into place.',
        'Through the peephole, the corridor is empty.',
        'Down on the street, footsteps pass by… and keep going.',
      ],
      hint: custom?.hint ?? (peak < 0.55 ? 'She never let him close the distance.' : 'That was close. Too close.'),
      stats: this.level.calm
        ? [
            ['Time walked', this.formatTime(this.elapsed)],
            ['Texts to Mom', `${this.mom.repliedTotal} sent · ${this.mom.ignoredTotal} ignored`],
            ['Dogs woken', `${this.stats.dogBarks}`],
            ['Bumped into', `${this.stats.bumps}×`],
          ]
        : [
            ['Time walked', this.formatTime(this.elapsed)],
            ['Closest he came', Number.isFinite(this.stats.closest) ? `${Math.max(1, Math.round(this.stats.closest))} m` : '—'],
            ['Looked back', `${this.stats.lookBacks}×`],
            ['Texts to Mom', `${this.mom.repliedTotal} sent · ${this.mom.ignoredTotal} ignored`],
            ['Dogs woken', `${this.stats.dogBarks}`],
            ['Bumped into', `${this.stats.bumps}×`],
            ['Felt', feeling],
          ],
      win: true,
      next: this.campaign.hasNext ? `Day ${this.campaign.current + 2}` : null,
    });
  }

  // ------------------------------------------------------------ camera + visibility

  private updateCamera(dt: number): void {
    const h = this.heroine;
    if (this.phase === 'title') {
      const t = this.time * 0.08;
      const spot = this.titleSpot;
      const cam = this.titleCam;
      this.camera.position.set(cam.x + Math.sin(t) * TITLE_SWAY, cam.y + Math.sin(t * 0.7) * 0.1, cam.z);
      this.camera.lookAt(spot.x - 0.4, 2.4, spot.z - 14);
      this.updateTitleScene(dt);
    } else {
      const lookBack = this.phase === 'caught' ? this.endTime > 0.15 : this.lookingBack;
      const fov = 60 - this.tension * 6;
      const shake = this.tension > 0.6 ? (this.tension - 0.6) * 2.2 : 0;
      const glancing = this.phase === 'playing' && this.elapsed < this.glanceUntil;
      if (glancing) this.glanceAt.copy(this.stranger.position).setY(GLANCE_HEIGHT);
      this.rig.update(dt, h.position, h.heading, lookBack && !glancing, this.map, this.reducedMotion ? 0 : shake, fov, glancing ? this.glanceAt : null);
    }
    if (this.debugCam) {
      this.camera.position.copy(h.position).add(this.debugCam.offset);
      this.camera.lookAt(this.tmpV.copy(h.position).add(this.debugCam.look));
    }
    this.moon.position.set(h.position.x - 30, 60, h.position.z - 40);
    this.moon.target.position.set(h.position.x, 0, h.position.z);
    this.camera.updateMatrixWorld();
  }

  /** Stand her a few steps back, out of shot, ready to walk in. */
  /**
   * Streets to home from her cell. The road-works cell is half open, so she can
   * stand in it although routing treats it as blocked: use its best neighbour.
   */
  private homeSteps(): number {
    const { c, r } = this.cell;
    const d = this.homeField[this.map.index(c, r)];
    if (d >= 0) return d;
    let best = -1;
    for (const [dc, dr] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      if (!this.map.inBounds(c + dc, r + dr)) continue;
      const n = this.homeField[this.map.index(c + dc, r + dr)];
      if (n >= 0 && (best < 0 || n < best)) best = n;
    }
    return best < 0 ? 0 : best + 1;
  }

  private startTitleScene(): void {
    const h = this.heroine;
    this.titleSpot.copy(h.position);
    let back = 0;
    for (let d = 5.5; d >= 0; d -= 0.5) {
      if (this.map.isWalkable(h.position.x, h.position.z + d)) {
        back = d;
        break;
      }
    }
    h.reset(h.position.x, h.position.z + back, 0);
    h.setGait(1);
    this.titleTime = 0;
    this.titleStopAt = -1;
    this.placeTitleCamera();
  }

  /**
   * Find a camera spot beside and behind her with a clear view up the street:
   * inside the street (never in a building), and with as little as possible
   * (poles, bins, walls, awnings) close to the lens in the part of the frame the
   * title text doesn't cover.
   */
  private placeTitleCamera(): void {
    const spot = this.titleSpot;
    const near = this.nearbyGeometry(spot, 12);
    const ray = new THREE.Raycaster();
    const cam = this.camera.clone();
    const ndc = new THREE.Vector2();
    const hits: THREE.Intersection[] = [];
    // Visible part of the shot: centre to right, horizon band down to her feet.
    const blockedRays = (x: number, z: number): number => {
      cam.position.set(x, 1.35, z);
      cam.lookAt(spot.x - 0.4, 2.4, spot.z - 14);
      cam.updateMatrixWorld();
      let blocked = 0;
      for (let i = 0; i <= 3; i += 1) {
        for (let j = 0; j <= 2; j += 1) {
          ray.setFromCamera(ndc.set(-0.3 + i * 0.25, -0.5 + j * 0.4), cam);
          ray.far = 5;
          hits.length = 0;
          near.raycast(ray, hits);
          // Ignore the road and kerbs: only things standing up into the shot count.
          if (hits.some((h) => h.point.y > 0.3)) blocked += 1;
        }
      }
      return blocked;
    };
    const fits = (x: number, z: number): boolean =>
      this.map.isWalkable(x - TITLE_SWAY, z, 0.4) && this.map.isWalkable(x + TITLE_SWAY, z, 0.4);
    // Preferred framing first (her a little left of centre); a spot must beat it clearly to win.
    let best: [number, number] | null = null;
    let bestScore = Infinity;
    for (const dz of [4.2, 3.4, 5.2, 2.6]) {
      for (const dx of [2.6, 2.0, 1.4, -2.0, -1.4, 0.8, -0.8]) {
        const x = spot.x + dx;
        const z = spot.z + dz;
        if (!fits(x, z)) continue;
        const score = blockedRays(x, z);
        if (score < bestScore - 1) {
          best = [x, z];
          bestScore = score;
        }
        if (bestScore === 0) break;
      }
      if (bestScore === 0) break;
    }
    near.geometry.dispose();
    (near.material as THREE.Material).dispose();
    const [x, z] = best ?? [spot.x + 1.2, spot.z + 4.2];
    this.titleCam.set(x, 1.35, z);
  }

  /** The world's solid triangles within `radius` of a point, as one mesh that's cheap to raycast. */
  private nearbyGeometry(at: THREE.Vector3, radius: number): THREE.Mesh {
    const out: number[] = [];
    const v = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];
    const box = new THREE.Box3();
    const area = new THREE.Box3(
      new THREE.Vector3(at.x - radius, -Infinity, at.z - radius),
      new THREE.Vector3(at.x + radius, Infinity, at.z + radius),
    );
    this.world?.group.updateMatrixWorld(true);
    this.world?.group.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh || (mesh as THREE.InstancedMesh).isInstancedMesh) return;
      if ((mesh.material as THREE.Material).transparent) return;
      const geom = mesh.geometry;
      const pos = geom.getAttribute('position');
      if (!pos || pos.itemSize !== 3 || (pos as THREE.InterleavedBufferAttribute).isInterleavedBufferAttribute) return;
      if (!geom.boundingBox) geom.computeBoundingBox();
      box.copy(geom.boundingBox!).applyMatrix4(mesh.matrixWorld);
      if (!box.intersectsBox(area)) return;
      const arr = pos.array as ArrayLike<number>;
      const idx = geom.getIndex()?.array as ArrayLike<number> | undefined;
      const count = idx ? idx.length : pos.count;
      const m = mesh.matrixWorld;
      const identity = m.equals(IDENTITY);
      for (let t = 0; t + 2 < count; t += 3) {
        let inside = false;
        for (let k = 0; k < 3; k += 1) {
          const vi = (idx ? idx[t + k] : t + k) * 3;
          v[k].set(arr[vi], arr[vi + 1], arr[vi + 2]);
          if (!identity) v[k].applyMatrix4(m);
          if (Math.abs(v[k].x - at.x) < radius && Math.abs(v[k].z - at.z) < radius) inside = true;
        }
        if (inside) for (const p of v) out.push(p.x, p.y, p.z);
      }
    });
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.Float32BufferAttribute(out, 3));
    return new THREE.Mesh(geom, new THREE.MeshBasicMaterial({ side: THREE.DoubleSide }));
  }

  /**
   * Once her model has loaded (or after a few seconds without it), she walks
   * up the street into the shot, stops, and gets her phone out.
   */
  private updateTitleScene(dt: number): void {
    const h = this.heroine;
    const ready = h.hasModel || this.time > 6;
    h.group.visible = ready;
    if (!ready) return;
    this.titleTime += dt;
    const left = h.position.z - this.titleSpot.z;
    if (this.titleStopAt < 0 && left <= 0.25) this.titleStopAt = this.titleTime;
    const stopped = this.titleStopAt >= 0;
    const phone = stopped && this.titleTime - this.titleStopAt > 0.9;
    h.update(
      dt,
      { steer: 0, lookBack: false, phone, headYaw: stopped ? 0 : Math.sin(this.titleTime * 0.9) * 0.25 },
      this.map,
      stopped,
    );
  }

  private updateVisibility(): void {
    this.projScreen.multiplyMatrices(this.camera.projectionMatrix, this.camera.matrixWorldInverse);
    this.frustum.setFromProjectionMatrix(this.projScreen);
    if (!this.stranger.isPresent || !this.stranger.group.visible) {
      this.strangerVisible = false;
      return;
    }
    const p = this.tmpV.copy(this.stranger.position);
    p.y = 1.3;
    this.strangerVisible = this.isPointVisible(p);
  }

  private isPointVisible(p: THREE.Vector3): boolean {
    const cam = this.camera.position;
    if (cam.distanceTo(p) > 58) return false;
    if (!this.frustum.containsPoint(p)) return false;
    return this.map.lineOfSight(cam.x, cam.z, p.x, p.z);
  }

  // ------------------------------------------------------------ misc

  private clockText(): string {
    const minutes = this.level.clockStart + Math.floor(this.elapsed / 20);
    const hh = Math.floor(minutes / 60) % 24;
    const mm = minutes % 60;
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  }

  private formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  }

  private updateDebug(): void {
    if (!this.debugTools.enabled) return;
    const st = this.stranger;
    this.hud.setDebug(
      [
        `phase   ${this.phase}`,
        `night   ${this.level.night}  zone ${this.act}   cell ${this.cell.c},${this.cell.r}`,
        `tension ${this.tension.toFixed(3)}`,
        `light   ${this.lightLevel.toFixed(2)} ${this.lit ? 'LIT' : 'dark'}${this.inShrine ? ' shrine' : ''}`,
        `him     ${st.state}  gap ${st.gap.toFixed(1)} / ${st.gapTarget.toFixed(1)}  v ${st.speed.toFixed(2)}`,
        `seen    ${this.strangerVisible}`,
        `speed   ${this.heroine.speed.toFixed(2)}`,
      ].join('\n'),
    );
  }

  /** What the dev sandbox panel can do. */
  private sandboxActions(): SandboxActions {
    const playing = (): boolean => this.phase === 'playing';
    return {
      nights: LEVELS.length,
      startNight: (i) => {
        this.campaign.unlocked = Math.max(this.campaign.unlocked, i + 1);
        this.campaign.select(i);
        this.hud.setNights(LEVELS, this.campaign.unlocked, this.campaign.current, (n) => this.selectNight(n));
        this.loadLevel(this.campaign.level);
        this.restart();
      },
      setSafe: (safe) => {
        this.sandboxSafe = safe;
      },
      setQuiet: (quiet) => {
        this.sandboxQuiet = quiet;
        if (!quiet) return;
        if (this.mom.texting) this.mom.cancelTyping();
        this.mom.reset();
        this.chatOpen = false;
        this.hud.setChat(null);
      },
      call: () => {
        if (playing() && !this.phoneFx.busy) this.runScare({ kind: 'call' });
      },
      mapFlash: () => {
        if (!playing() || this.phoneFx.busy) return;
        // Get the map up first, then break into it.
        this.chatOpen = false;
        this.phoneLocal = true;
        window.setTimeout(() => {
          if (playing() && !this.phoneFx.busy) this.runScare({ kind: 'flash' });
        }, 800);
      },
      unknownText: (text) => {
        if (this.link.connected) this.runScare({ kind: 'text', text });
        else this.hud.say('[sandbox] No phone linked: these texts only go to a linked phone.', 3, true);
      },
      photo: () => {
        if (playing()) this.sendNote(`sandbox-photo-${this.elapsed}`, 'Unknown', '[Photo] 📷');
      },
      figure: () => {
        if (playing() && !this.unease.forceFigure(this.heroine.position, this.heroine.forward, this.map)) {
          this.hud.say('[sandbox] No open street ahead for the figure. Face down a road.', 3, true);
        }
      },
      himAhead: () => {
        if (!playing()) return;
        const h = this.heroine.position;
        const f = this.heroine.forward;
        this.stranger.appearAhead(h.x + f.x * 18, h.z + f.z * 18, h);
      },
      caught: () => {
        if (!playing()) return;
        const safe = this.sandboxSafe;
        this.sandboxSafe = false;
        this.startCaught('caught');
        this.sandboxSafe = safe;
      },
      whisper: () => this.audio.whisper(0.6, 1),
      extraStep: () => this.audio.phantomStep(0.7, 0.3, 0.3),
      distant: (kind) => this.audio.distant(kind, 0.9, 0.5),
      lampStutter: () => {
        const h = this.heroine.position;
        let best: LightSource | null = null;
        let bestD = 40;
        for (const l of this.world?.lights.sources ?? []) {
          const d = Math.hypot(l.ground.x - h.x, l.ground.y - h.z);
          if (l.state === 'on' && d < bestD) {
            best = l;
            bestD = d;
          }
        }
        if (!best) return;
        best.disturb(1.4);
        this.audio.lampZap(Math.max(0.2, 1 - bestD / 30), 0);
      },
    };
  }

  private installTestHooks(): void {
    window.__THREE_GAME_TEST_HOOKS__ = {
      seed: (value: number) => {
        this.rng = createSeededRandom(value);
      },
      setState: (name: string) => {
        if (!['active-play', 'complete', 'caught', 'title'].includes(name)) throw new Error(`Unknown test state: ${name}`);
        this.hud.showTitle(false);
        this.resetRun();
        this.hud.fade(0, 0);
        if (name === 'title') {
          this.phase = 'title';
          this.startTitleScene();
          this.hud.showTitle(true);
          this.hud.setHud(false);
        } else if (name === 'active-play') {
          this.phase = 'playing';
        } else if (name === 'complete') {
          this.phase = 'home';
          this.showHomeEnd();
        } else {
          this.phase = 'caught';
          this.endTime = 3;
          this.endShown = false;
          this.updateCaught(0);
        }
        this.updateCamera(0.016);
        this.render();
        this.publishDiagnostics();
        return { state: name };
      },
      setPausedForScreenshot: (paused: boolean) => {
        this.pausedForScreenshot = paused;
      },
      setReducedMotion: (enabled: boolean) => {
        this.reducedMotion = enabled;
        this.render();
      },
      hideDebugUi: (hidden: boolean) => {
        this.debugTools.setHidden(hidden);
        if (hidden) this.hud.setDebug(null);
      },
      teleport: (c: number, r: number, heading = 0) => {
        const p = this.map.center(c, r, this.tmpV);
        this.heroine.position.set(p.x, 0, p.z);
        this.heroine.heading = heading;
        // Arrive like she walked there, so zone and cell story hooks fire.
        const prev = this.cell;
        this.cell = this.map.cellAt(p.x, p.z);
        this.onCellChange(prev, this.cell);
        this.trail.clear();
        this.trail.record(this.heroine.position);
        this.rig.snap(this.heroine.position, heading);
      },
      setTension: (value: number) => {
        this.tension = value;
      },
      setBattery: (percent: number) => {
        this.battery = percent;
      },
      /** No Mom texts this run (for screenshots). */
      quietMom: () => {
        this.mom.reset();
        this.chatOpen = false;
        this.phoneApp = 'none';
        this.hud.setChat(null);
      },
      hazards: () => ({ dogs: this.hazards.dogInfo(), barks: this.stats.dogBarks, bumps: this.stats.bumps }),
      hurryWalker: () => this.hazards.hurryWalker(),
      /** Stand him `ahead` metres up the street from her, waiting with his phone out. */
      setExposure: (value: number) => {
        this.tuning.exposure = value;
        this.renderer.toneMappingExposure = value;
      },
      worldSpots: (kind: string) =>
        kind === 'figure'
          ? [[this.apparition.position.x, this.apparition.position.y, this.apparition.position.z]]
          : kind === 'perch'
          ? (this.world?.perches ?? []).map((p) => [p.x, p.y, p.z])
          : (this.world?.props ?? []).filter((p) => p.kind === kind).map((p) => [p.x, p.y, p.z]),
      strangerAhead: (ahead: number) => {
        const h = this.heroine.position;
        this.stranger.appearAhead(h.x + this.heroine.forward.x * ahead, h.z + this.heroine.forward.z * ahead, h, true);
      },
      hurryFigure: () => this.unease.hurryFigure(),
      phoneScare: (kind: 'text' | 'call' | 'flash' | 'photo') => {
        if (kind === 'photo') this.sendNote(`photo-test-${this.elapsed}`, 'Unknown', '[Photo] 📷');
        else if (kind === 'text') this.runScare({ kind, text: 'why did you stop?' });
        else this.runScare({ kind });
      },
      hurryPhone: () => this.scares.hurry(),
      faceImage: () => {
        this.setStrangerFace();
        return this.strangerFace?.toDataURL() ?? '';
      },
      debugCamera: (offset: [number, number, number] | null, look: [number, number, number] = [0, 1.2, 0]) => {
        this.debugCam = offset ? { offset: new THREE.Vector3(...offset), look: new THREE.Vector3(...look) } : null;
      },
      setLevel: (index: number) => {
        if (index < 0 || index >= LEVELS.length) throw new Error(`No level ${index}`);
        this.campaign.unlocked = Math.max(this.campaign.unlocked, index + 1);
        this.campaign.select(index);
        this.loadLevel(this.campaign.level);
        this.resetRun();
        return LEVELS[index].id;
      },
    };
  }

  private publishDiagnostics(): void {
    const info = this.renderer.info;
    const h = this.heroine;
    window.__THREE_GAME_DIAGNOSTICS__ = {
      frame: this.frame,
      elapsed: this.elapsed,
      phase: this.phase,
      tension: this.tension,
      act: this.act,
      lit: this.lit,
      battery: this.battery,
      flashlight: this.flashlightOn,
      stranger: { state: this.stranger.state, gap: this.stranger.gap, visible: this.strangerVisible, boost: this.stranger.boostTime, creep: this.stranger.creep, x: this.stranger.position.x, z: this.stranger.position.z, glance: this.elapsed < this.glanceUntil, glances: this.glanceCount },
      figure: this.apparition.visible,
      phoneScare: this.phoneFx.stage,
      player: {
        position: { x: h.position.x, y: h.position.y, z: h.position.z },
        speed: h.speed,
        heading: h.heading,
      },
      renderer: {
        calls: info.render.calls,
        triangles: info.render.triangles,
        geometries: info.memory.geometries,
        textures: info.memory.textures,
      },
      canvas: {
        clientWidth: this.canvas.clientWidth,
        clientHeight: this.canvas.clientHeight,
        width: this.canvas.width,
        height: this.canvas.height,
        dpr: Math.min(window.devicePixelRatio || 1, this.tuning.maxDpr),
      },
    };
  }
}
